<template>
  <div class="cms-page">
    <div class="cms-header">
      <div class="cms-header-left">
        <div class="cms-title-wrapper">
          <div class="cms-icon-box">
            <i class="mdi mdi-view-dashboard-edit-outline"></i>
          </div>
          <div>
            <h1 class="cms-title">
              Landing Page Builder
            </h1>
            <p class="cms-subtitle">
              Drag Section from the left → drop onto the canvas → edit inline
            </p>
          </div>
        </div>
      </div>
      <div class="cms-header-actions">
        <button class="btn-header btn-header-save" :disabled="saving" @click="saveAll">
          <i class="mdi mdi-content-save-outline"></i>
          {{ saving ? 'Saving…' : 'Save All' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="cms-loading">
      <div class="loading-spinner"></div>
      <p>Loading sections…</p>
    </div>

    <div v-else-if="error" class="cms-error">
      <i class="mdi mdi-alert-circle-outline"></i>
      <p>{{ error }}</p>
      <button class="btn-retry" @click="getSectionAll">
        Retry
      </button>
    </div>

    <div v-else class="cms-builder">
      <aside class="sidebar">
        <div class="sidebar-section">
          <p class="sidebar-label">
            AVAILABLE SECTION
          </p>
          <p class="sidebar-hint">
            Drag a section onto the canvas
          </p>
          <div class="section-library">
            <div
              class="section-chip"
              draggable="true"
              @dragstart="onDragNewSection($event, { type: 'custom', label: 'Add Section', icon: 'mdi-plus-box-outline' })"
            >
              <i class="mdi mdi-plus-box-outline"></i>
              Add Section
            </div>
            <div
              v-for="sectionType in sectionTypes"
              :key="sectionType.type"
              class="section-chip"
              draggable="true"
              @dragstart="onDragNewSection($event, sectionType)"
            >
              <i :class="`mdi ${sectionType.icon}`"></i>
              {{ sectionType.label }}
            </div>
          </div>
        </div>

        <div class="sidebar-section">
          <p class="sidebar-label">
            HIDDEN SECTIONS
          </p>
          <p class="sidebar-hint">
            Click to restore to canvas
          </p>
          <div class="section-library">
            <div
              v-for="section in hiddenSections"
              :key="section.section_key"
              class="section-chip"
              @click="restoreSection(section)"
            >
              <i :class="`mdi ${section.icon || 'mdi-eye-off-outline'}`"></i>
              <span>{{ section.nav_label || section.section_name || section.section_key }}</span>
              <i class="mdi mdi-restore" style="margin-left: auto; color: #0B6599;"></i>
            </div>
          </div>
        </div>
      </aside>

      <main
        class="canvas"
        @dragover.prevent="onCanvasDragOver($event)"
        @drop="onCanvasDrop($event)"
      >
        <div
          v-if="!visibleSections.length"
          class="canvas-empty"
        >
          <i class="mdi mdi-arrow-left-circle-outline"></i>
          <p>Drag a block here to start building</p>
        </div>

        <div
          class="drop-indicator"
          v-if="dropIndex === 0 && draggingOver"
        ></div>

        <div v-for="(section, idx) in visibleSections" :key="section.section_key">
          <div
            class="canvas-block"
            :class="{ 'canvas-block--active': activeKey === section.section_key }"
            draggable="true"
            @dragstart="onDragCanvasBlock($event, section)"
            @dragover.prevent="onDragOverBlock($event, idx)"
            @drop.stop="onDropOnBlock($event, idx)"
            @click="setActive(section.section_key)"
          >
            <div class="block-toolbar">
              <span class="block-toolbar-handle">
                <i class="mdi mdi-drag-vertical"></i>
              </span>
              <span class="block-toolbar-key">
                {{ section.section_key }}
              </span>
              <span class="block-toolbar-spacer"></span>
              <button class="toolbar-btn" title="Move up" @click.stop="moveUp(idx)" :disabled="idx === 0">
                <i class="mdi mdi-arrow-up"></i>
              </button>
              <button class="toolbar-btn" title="Move down" @click.stop="moveDown(idx)" :disabled="idx === visibleSections.length - 1">
                <i class="mdi mdi-arrow-down"></i>
              </button>
              <button class="toolbar-btn" title="Hide section" @click.stop="hideSection(section)">
                <i class="mdi mdi-eye-off-outline"></i>
              </button>
              <button class="toolbar-btn toolbar-btn--danger" title="Delete section" @click.stop="deleteSection(section)">
                <i class="mdi mdi-trash-can-outline"></i>
              </button>
            </div>

            <div class="block-editor" v-if="activeKey === section.section_key">
              <div class="editor-cols">
                <div class="editor-col" @click.stop>
                  <p class="editor-col-title">
                    Section Content
                  </p>
                  <div class="field-group" v-if="section.section_name !== undefined">
                    <label class="field-label">
                      Section Name
                    </label>
                    <input v-model="section.section_name" @input="autoGenerateKey(section)" class="field-input" placeholder="e.g. Homepage" />
                  </div>
                  <div class="field-group" v-if="section.nav_label !== undefined">
                    <label class="field-label">
                      Nav Label
                    </label>
                    <input v-model="section.nav_label" class="field-input" placeholder="Label di navbar" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">
                      Icon
                    </label>
                    <input v-model="section.icon" class="field-input" placeholder="mdi-xxx" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">
                      Subtitle
                    </label>
                    <input v-model="section.subtitle" class="field-input" placeholder="e.g. WHAT IS MEDAN 247?" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">
                      Title
                    </label>
                    <input v-model="section.title" class="field-input" placeholder="Main heading" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">
                      Description
                    </label>
                    <textarea v-model="section.description" class="field-textarea" rows="3" placeholder="Body text…"></textarea>
                  </div>

                  <button class="btn-save-section" :disabled="section._saving" @click.stop="saveSection(section)">
                    <i class="mdi mdi-content-save-outline"></i>
                    {{ section._saving ? 'Saving…' : 'Save Section' }}
                  </button>
                </div>

                <div class="editor-col">
                  <div class="items-header">
                    <p class="editor-col-title">
                      Items ({{ section.items.length }})
                    </p>
                    <button class="btn-add-block-item" @click.stop="openBlockPicker(section)">
                      <i class="mdi mdi-plus-box-multiple-outline"></i> Add Block Item
                    </button>
                  </div>

                  <div class="block-picker-overlay" v-if="blockPicker.show" @click.self="closeBlockPicker">
                    <div class="block-picker-dialog" @click.stop>
                      <div class="block-picker-header">
                        <h3 class="block-picker-title">
                          Add Block Item
                        </h3>
                        <button class="block-picker-close" @click="closeBlockPicker">
                          <i class="mdi mdi-close"></i>
                        </button>
                      </div>
                      <p class="block-picker-hint">
                        Select a template block to add to the section.
                      </p>

                      <div class="block-picker-list">
                        <div
                          v-for="block in blockTemplates"
                          :key="block.id"
                          class="block-picker-item"
                          :class="{ 'block-picker-item--selected': blockPicker.selectedId === block.id }"
                          @click="blockPicker.selectedId = block.id"
                        >
                          <div class="block-picker-item-header">
                            <i :class="`mdi ${block.icon}`"></i>
                            <span class="block-picker-item-label">
                              {{ block.label }}
                            </span>
                          </div>
                          <div class="block-picker-item-preview">
                            <div
                              v-for="(row, idx) in block.preview"
                              :key="idx"
                              class="block-picker-preview-row"
                            >
                              <i v-if="row.icon" :class="`mdi ${row.icon}`"></i>
                              <span>{{ row.text }}</span>
                              <span v-if="row.itemImage" class="block-picker-preview-img-badge">
                                <i class="mdi mdi-image-outline"></i> {{ row.itemImage }} image{{ row.itemImage > 1 ? 's' : '' }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="block-picker-footer">
                        <button class="btn-block-cancel" @click="closeBlockPicker">
                          Cancel
                        </button>
                        <button
                          class="btn-block-confirm"
                          :disabled="!blockPicker.selectedId"
                          @click="addBlockItems"
                        >
                          <i class="mdi mdi-plus"></i> Add Block
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    v-for="(group, gIdx) in getDisplayGroups(section)"
                    :key="gIdx"
                  >
                    <div
                      :class="[
                        'item-block',
                        (group.headline && group.items.length > 0) || (!group.headline && group.items.length > 1)
                          ? 'item-block--grouped'
                          : ''
                      ]"
                    >
                      <div
                        v-if="(group.headline && group.items.length > 0) || (!group.headline && group.items.length > 1)"
                        class="item-block-header"
                      >
                        <button class="btn-add-item-group" @click.stop="addItemToGroup(group, section)">
                          <i class="mdi mdi-plus"></i> Add Item
                        </button>
                        <button class="btn-delete-block" @click.stop="deleteBlock(group, section)" title="Delete block">
                          <i class="mdi mdi-trash-can-outline"></i>
                        </button>
                      </div>

                      <div
                        v-if="group.headline"
                        :key="group.headline._key"
                        class="item-row"
                        draggable="true"
                        @dragstart.stop="onDragItem($event, group.headline, section)"
                        @dragover.prevent.stop="onDragOverItem($event, group.headline, section)"
                        @drop.stop="onDropOnItem($event, group.headline, section)"
                      >
                        <i class="mdi mdi-drag-vertical item-drag"></i>
                        <div class="item-fields">
                          <div class="item-field-grid">
                            <input
                              v-if="group.headline._hasIconField && !group.headline._hasRoleField && group.headline.title !== 'TEAM'"
                              v-model="group.headline.icon"
                              class="field-input field-input--sm"
                              placeholder="icon (mdi-xxx)"
                              @click.stop
                            />
                            <input
                              v-if="showTitleInput(group.headline)"
                              v-model="group.headline.title"
                              class="field-input field-input--sm"
                              :class="{ 'field-input--full': isNewsletterItem(group.headline) }"
                              placeholder="title"
                              @click.stop
                            />
                            <input
                              v-if="group.headline._hasRoleField"
                              v-model="group.headline.role"
                              class="field-input field-input--sm"
                              placeholder="role"
                              @click.stop
                            />
                            <input
                              v-if="group.headline._hasTextField && !group.headline._hasRoleField && !isNewsletterItem(group.headline)"
                              v-model="group.headline.text"
                              class="field-input field-input--sm"
                              :placeholder="isSocialOrLink(group.headline) ? 'url' : 'text'"
                              @click.stop
                            />
                          </div>

                          <div class="item-photo-row" v-if="!group.isImageGallery && hasImageSlot(group.headline)">
                            <div v-if="group.headline.image" class="drop-zone-uploaded">
                              <img
                                :src="group.headline.image.startsWith('blob:') ? group.headline.image : `${baseURL}/${group.headline.image}`"
                                class="item-photo"
                                :class="{ 'item-photo--wide': isHeadlineItem(group.headline) }"
                              />
                              <button class="btn-remove-image" @click.stop="removeImage(group.headline)">
                                Remove Image
                              </button>
                            </div>
                            <div
                              v-else
                              :class="['drop-zone-upload', group.headline._dragOver ? 'drop-zone-upload-over' : '']"
                              @dragenter.prevent="group.headline._dragOver = true"
                              @dragover.prevent
                              @dragleave.prevent="group.headline._dragOver = false"
                              @drop.prevent="handleDropPhoto($event, group.headline)"
                            >
                              <div class="drop-zone-upload-info">
                                <i class="mdi mdi-cloud-upload-outline drop-zone-icon"></i>
                                <div class="drop-zone-title">
                                  {{ isNewsletterItem(group.headline) ? 'Drop newsletter image or click to input' : 'Drop image or click to input' }}
                                </div>
                              </div>
                              <input type="file" accept="image/*" @change="(e) => onPhotoChange(e, group.headline)" @click.stop />
                            </div>
                          </div>

                          <div class="item-photo-row" v-if="isNewsletterItem(group.headline)">
                            <div v-if="group.headline.pdf" class="drop-zone-uploaded">
                              <div class="pdf-preview">
                                <i class="mdi mdi-file-pdf-box" style="font-size: 32px; color: #e53935;"></i>
                                <span class="pdf-filename">
                                  {{ group.headline.pdf.split('/').pop() }}
                                </span>
                              </div>
                              <button class="btn-remove-image" @click.stop="removePdf(group.headline)">
                                Remove Newsletter
                              </button>
                            </div>
                            <div
                              v-else
                              :class="['drop-zone-upload', group.headline._dragOverPdf ? 'drop-zone-upload-over' : '']"
                              @dragenter.prevent="group.headline._dragOverPdf = true"
                              @dragover.prevent
                              @dragleave.prevent="group.headline._dragOverPdf = false"
                              @drop.prevent="handleDropPdf($event, group.headline)"
                            >
                              <div class="drop-zone-upload-info">
                                <i class="mdi mdi-file-upload-outline drop-zone-icon"></i>
                                <div class="drop-zone-title">
                                  Drop newsletter PDF or click to input
                                </div>
                              </div>
                              <input type="file" accept="application/pdf" @change="(e) => onPdfChange(e, group.headline)" @click.stop />
                            </div>
                          </div>
                        </div>
                        <div class="item-actions">
                          <button
                            class="item-btn item-btn--save"
                            :disabled="group.headline._saving"
                            @click.stop="saveItem(group.headline, section, group)"
                            title="Save item"
                          >
                            <i class="mdi mdi-content-save-outline"></i>
                          </button>
                          <button
                            class="item-btn item-btn--delete"
                            @click.stop="deleteItem(group.headline, section)"
                            title="Delete item"
                          >
                            <i class="mdi mdi-trash-can-outline"></i>
                          </button>
                        </div>
                      </div>

                      <div v-if="group.isImageGallery">
                        <div
                          v-for="slot in group.items"
                          :key="slot._slotIndex"
                          class="item-row"
                        >
                          <div class="item-fields">
                            <div class="item-photo-row">
                              <div v-if="slot.image" class="drop-zone-uploaded">
                                <img
                                  :src="slot.image.startsWith('blob:') ? slot.image : `${baseURL}/${slot.image}`"
                                  class="item-photo item-photo--wide"
                                />
                                <button class="btn-remove-image" @click.stop="removeImage(slot)">
                                  Remove Image
                                </button>
                              </div>
                              <div
                                v-else
                                :class="['drop-zone-upload', slot._dragOver ? 'drop-zone-upload-over' : '']"
                                @dragenter.prevent="slot._dragOver = true"
                                @dragover.prevent
                                @dragleave.prevent="slot._dragOver = false"
                                @drop.prevent="handleDropPhoto($event, slot)"
                              >
                                <div class="drop-zone-upload-info">
                                  <i class="mdi mdi-cloud-upload-outline drop-zone-icon"></i>
                                  <div class="drop-zone-title">
                                    Drop image or click to input
                                  </div>
                                </div>
                                <input type="file" accept="image/*" @change="(e) => onPhotoChange(e, slot)" @click.stop />
                              </div>
                            </div>
                          </div>
                          <div class="item-actions">
                            <button
                              class="item-btn item-btn--save"
                              :disabled="slot._saving"
                              @click.stop="saveItem(slot, section, group)"
                              title="Save item"
                            >
                              <i class="mdi mdi-content-save-outline"></i>
                            </button>
                            <button
                              class="item-btn item-btn--delete"
                              :disabled="slot._parentItem.item_image <= 2"
                              @click.stop="deleteItem(slot, section)"
                              title="Delete item"
                            >
                              <i class="mdi mdi-trash-can-outline"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <template v-else>
                        <div
                          v-for="item in group.items"
                          :key="item._key"
                          class="item-row"
                          draggable="true"
                          @dragstart.stop="onDragItem($event, item, section)"
                          @dragover.prevent.stop="onDragOverItem($event, item, section)"
                          @drop.stop="onDropOnItem($event, item, section)"
                        >
                          <i class="mdi mdi-drag-vertical item-drag"></i>
                          <div class="item-fields">
                            <div class="item-field-grid">
                              <input
                                v-if="item._hasIconField && !item._hasRoleField && item.title !== 'TEAM'"
                                v-model="item.icon"
                                class="field-input field-input--sm"
                                placeholder="icon (mdi-xxx)"
                                @click.stop
                              />
                              <input
                                v-if="showTitleInput(item)"
                                v-model="item.title"
                                class="field-input field-input--sm"
                                :class="{ 'field-input--full': isNewsletterItem(item) }"
                                placeholder="title"
                                @click.stop
                              />
                              <input
                                v-if="item._hasRoleField"
                                v-model="item.role"
                                class="field-input field-input--sm"
                                placeholder="role"
                                @click.stop
                              />
                              <input
                                v-if="item._hasTextField && !item._hasRoleField && !isNewsletterItem(item)"
                                v-model="item.text"
                                class="field-input field-input--sm"
                                :placeholder="isSocialOrLink(item) ? 'url' : 'text'"
                                @click.stop
                              />
                            </div>
                            <div class="item-photo-row" v-if="hasImageSlot(item)">
                              <div v-if="item.image" class="drop-zone-uploaded">
                                <img
                                  :src="item.image.startsWith('blob:') ? item.image : `${baseURL}/${item.image}`"
                                  class="item-photo"
                                  :class="{ 'item-photo--wide': isHeadlineItem(item) }"
                                />
                                <button class="btn-remove-image" @click.stop="removeImage(item)">
                                  Remove Image
                                </button>
                              </div>
                              <div
                                v-else
                                :class="['drop-zone-upload', item._dragOver ? 'drop-zone-upload-over' : '']"
                                @dragenter.prevent="item._dragOver = true"
                                @dragover.prevent
                                @dragleave.prevent="item._dragOver = false"
                                @drop.prevent="handleDropPhoto($event, item)"
                              >
                                <div class="drop-zone-upload-info">
                                  <i class="mdi mdi-cloud-upload-outline drop-zone-icon"></i>
                                  <div class="drop-zone-title">
                                    {{ isNewsletterItem(item) ? 'Drop newsletter image or click to input' : 'Drop image or click to input' }}
                                  </div>
                                </div>
                                <input type="file" accept="image/*" @change="(e) => onPhotoChange(e, item)" @click.stop />
                              </div>
                            </div>
                            <div class="item-photo-row" v-if="isNewsletterItem(item)">
                              <div v-if="item.pdf" class="drop-zone-uploaded">
                                <div class="pdf-preview">
                                  <i class="mdi mdi-file-pdf-box" style="font-size: 32px; color: #e53935;"></i>
                                  <span class="pdf-filename">
                                    {{ item.pdf.split('/').pop() }}
                                  </span>
                                </div>
                                <button class="btn-remove-image" @click.stop="removePdf(item)">
                                  Remove Newsletter
                                </button>
                              </div>
                              <div
                                v-else
                                :class="['drop-zone-upload', item._dragOverPdf ? 'drop-zone-upload-over' : '']"
                                @dragenter.prevent="item._dragOverPdf = true"
                                @dragover.prevent
                                @dragleave.prevent="item._dragOverPdf = false"
                                @drop.prevent="handleDropPdf($event, item)"
                              >
                                <div class="drop-zone-upload-info">
                                  <i class="mdi mdi-file-upload-outline drop-zone-icon"></i>
                                  <div class="drop-zone-title">
                                    Drop newsletter PDF or click to input
                                  </div>
                                </div>
                                <input type="file" accept="application/pdf" @change="(e) => onPdfChange(e, item)" @click.stop />
                              </div>
                            </div>
                          </div>
                          <div class="item-actions">
                            <button
                              class="item-btn item-btn--save"
                              :disabled="item._saving"
                              @click.stop="saveItem(item, section, group)"
                              title="Save item"
                            >
                              <i class="mdi mdi-content-save-outline"></i>
                            </button>
                            <button
                              v-if="group.headline && group.headline.title === 'BLOG'"
                              class="item-btn item-btn--edit"
                              @click.stop="editBlog(item)"
                              title="Edit blog"
                            >
                              <i class="mdi mdi-pencil-outline"></i>
                            </button>
                            <button
                              class="item-btn item-btn--delete"
                              :disabled="group.items.length <= 1"
                              @click.stop="deleteItem(item, section)"
                              title="Delete item"
                            >
                              <i class="mdi mdi-trash-can-outline"></i>
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>

                  <div v-if="!section.items.length" class="items-empty">
                    No items yet — click Add Block Item
                  </div>
                </div>
              </div>
            </div>

            <div class="block-preview" v-else>
              <p class="block-preview-subtitle">
                {{ section.subtitle || '—' }}
              </p>
              <p class="block-preview-title">
                {{ section.title || 'Untitled section' }}
              </p>
              <p class="block-preview-meta" v-if="section.items.length">
                {{ section.items.length }} item{{ section.items.length > 1 ? 's' : '' }}
              </p>
            </div>
          </div>

          <div
            class="drop-indicator"
            v-if="dropIndex === idx + 1 && draggingOver"
          ></div>
        </div>
      </main>
    </div>

    <div class="cms-toast" :class="{ 'cms-toast--show': toast.show, [`cms-toast--${toast.type}`]: true }">
      <i :class="`mdi ${toast.type === 'success' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'}`"></i>
      {{ toast.message }}
    </div>

    <v-dialog
      v-model="newsletterBlogConfirmDialog"
      max-width="420px"
      content-class="newsletter-confirm-dialog"
      persistent
    >
      <div class="newsletter-confirm-modal">
        <div class="newsletter-confirm-icon">
          <i class="mdi mdi-help-circle-outline"></i>
        </div>

        <h3 class="newsletter-confirm-title">
          Turn this newsletter into a blog?
        </h3>

        <p class="newsletter-confirm-description">
          Do you want this newsletter to also be published as a blog post?
          You can always edit it later from the Blog section.
        </p>

        <div class="newsletter-confirm-actions">
          <button
            type="button"
            class="newsletter-confirm-btn newsletter-confirm-btn-no"
            :disabled="newsletterConfirmLoading"
            @click="resolveNewsletterBlogConfirm(false)"
          >
            No, just save
          </button>
          <button
            type="button"
            class="newsletter-confirm-btn newsletter-confirm-btn-yes"
            :disabled="newsletterConfirmLoading"
            @click="resolveNewsletterBlogConfirm(true)"
          >
            <i v-if="newsletterConfirmLoading" class="mdi mdi-loading mdi-spin"></i>
            Yes, make it a blog
          </button>
        </div>
      </div>
    </v-dialog>

    <v-dialog
      v-model="proofreadWarningDialog"
      max-width="400px"
      content-class="proofread-warning-dialog"
    >
      <div class="proofread-warning-modal">
        <div class="proofread-warning-icon">
          <i class="mdi mdi-alert-outline"></i>
        </div>

        <h3 class="proofread-warning-title">
          Please Proofread Before Publishing
        </h3>

        <p class="proofread-warning-description">
          The content below was auto-extracted from your newsletter.
          Please review and proofread it carefully for mistakes before publishing.
        </p>

        <button
          type="button"
          class="proofread-warning-btn"
          @click="proofreadWarningDialog = false"
        >
          Got it
        </button>
      </div>
    </v-dialog>

    <v-dialog
      v-model="dialogWrongExtension"
      persistent
      max-width="330px"
    >
      <v-card
        class="text-center align-center justify-center pa-6 rounded-lg"
        style="border-radius: 30px"
        width="330px"
      >
        <v-avatar class="dialog-wrong-extension mb-4 mt-2" size="62">
          <v-icon x-large class="white--text">
            mdi-close
          </v-icon>
        </v-avatar>

        <div
          class="text-center text-h6 align-center justify-center"
          style="word-break: break-word"
        >
          Image you uploaded has the wrong extension. Allowed extension jpg, jpeg, png.
        </div>

        <v-divider class="mt-4"></v-divider>

        <v-card-actions class="align-center justify-center">
          <v-btn
            class="my-3 dialog-wrong-extension"
            width="180px"
            rounded
            dark
            @click="dialogWrongExtension = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogWrongExtensionNewsletter"
      persistent
      max-width="330px"
    >
      <v-card
        class="text-center align-center justify-center pa-6 rounded-lg"
        style="border-radius: 30px"
        width="330px"
      >
        <v-avatar class="dialog-wrong-extension mb-4 mt-2" size="62">
          <v-icon x-large class="white--text">
            mdi-close
          </v-icon>
        </v-avatar>

        <div
          class="text-center text-h6 align-center justify-center"
          style="word-break: break-word"
        >
          File you uploaded has the wrong extension. Only PDF files are allowed for newsletter.
        </div>

        <v-divider class="mt-4"></v-divider>

        <v-card-actions class="align-center justify-center">
          <v-btn
            class="my-3 dialog-wrong-extension"
            width="180px"
            rounded
            dark
            @click="dialogWrongExtensionNewsletter = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogDelete" width="330px">
      <v-card class="text-center align-center justify-center pa-6 rounded-lg" width="330px">
        <v-avatar class="color-warning my-4" size="62">
          <v-icon x-large class="white--text">
            mdi-alert-octagon
          </v-icon>
        </v-avatar>
        <div class="text-center text-h6 align-center justify-center font-weight-medium">
          Confirm Deletion
        </div>
        <v-card-text class="align-center justify-center mt-2">
          {{ deleteMessage }}
        </v-card-text>
        <v-divider class="mt-4"></v-divider>
        <v-card-actions class="align-center justify-center">
          <v-btn class="my-3 color-warning" width="90px" rounded dark @click="deleteItemConfirm">
            OK
          </v-btn>
          <v-btn class="my-3 color-warning-border" width="90px" rounded outlined dark @click="closeDelete">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <BlogEditorDialog
      v-model="blogDialogOpen"
      :mode="blogDialogMode"
      :saving="blogDialogMode === 'edit' ? savingEditBlog : creatingBlog"
      :toolbar="blogEditorToolbar"
      :initial-data="blogDialogInitialData"
      @image-added="handleInlineImageUpload"
      @toast="({ message, type }) => showToast(message, type)"
      @save="handleBlogSave"
      @close="handleBlogDialogClose"
    />

    <div>
      <Notification ref="alert"></Notification>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import runtimeConfig from '../configRuntime.js';
import BlogEditorDialog from '@/components/BlogEditorDialog';
import Notification from '@/components/Notification';
const auth = () => ({ 'auth-token': localStorage.getItem('token_app') || '' });
let _key = 0;
const nextKey = () => ++_key;

export default {
  name: 'LandingPageCMS',
  components: {
    BlogEditorDialog,
    Notification: Notification
  },
  data() {
    return {
      skipBlogPromptKeys: new Set(),
      newsletterBlogConfirmDialog: false,
      newsletterConfirmLoading: false,
      newsletterBlogConfirmResolver: null,
      pendingNewsletterConfirmItem: null,
      pendingNewsletterConfirmSection: null,
      pendingNewsletterSave: null,
      pendingNewsletterImagePath: null,
      proofreadWarningDialog: false,
      blogDialogOpen: false,
      blogDialogMode: 'create',
      blogDialogInitialData: {},
      creatingBlog: false,
      savingEditBlog: false,
      blogEditorToolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ header: [1, 2, 3, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image"],
        ["clean"]
      ],
      loading: false,
      error: null,
      saving: false,
      allSections: [],
      templateSections: [],
      blockTemplates: [],
      activeKey: null,
      addingBlock: false,
      deletingBlock: false,
      pendingActions: new Set(),
      addItemGroupLocks: new Set(),
      blockPicker: {
        show: false,
        section: null,
        selectedId: null,
      },
      draggedBlock: null,
      draggingOver: false,
      dropIndex: null,
      draggingNewBlock: null,
      draggedItem: null,
      draggedItemSection: null,
      headlineImageTitles: new Set(),
      dialogWrongExtension: false,
      dialogWrongExtensionNewsletter: false,
      dialogDelete: false,
      deleteTarget: null,
      deleteResolver: null,
      slotCache: {},
      baseURL: runtimeConfig.VUE_APP_API_SOCKET,
      toast: { show: false, message: '', type: 'success' },
    };
  },
  computed: {
    sectionTypes() {
      return this.allSections
        .filter(s => s.is_active)
        .map(s => ({
          type: s.section_key,
          label: s.nav_label || s.section_name || s.section_key,
          icon: s.icon || 'mdi-view-grid-outline',
        }));
    },
    visibleSections() {
      return this.allSections.filter(s => s.is_active);
    },
    hiddenSections() {
      return this.allSections.filter(s => !s.is_active);
    },
    deleteMessage() {
      if (!this.deleteTarget) return 'Are you sure you want to permanently delete this item?';
      if (this.deleteTarget.type === 'section') {
        return `Delete section "${this.deleteTarget.label}"? This cannot be undone.`;
      }
      if (this.deleteTarget.type === 'item') {
        return `Delete item "${this.deleteTarget.label}"?`;
      }
      if (this.deleteTarget.type === 'block') {
        return `Delete block "${this.deleteTarget.label}" dan semua item di dalamnya? (${this.deleteTarget.count} item)`;
      }
      return 'Are you sure you want to permanently delete this item?';
    },
  },
  created() {
    this.getSectionAll();
  },
  methods: {
    hasImageSlot(item) {
      return item._hasRoleField || Number(item.item_image) >= 1 || this.isNewsletterItem(item);
    },
    isNewsletterGroup(group) {
      const headlineTitle = group?.headline?.title || '';
      return headlineTitle === 'NEWSLETTER';
    },
    askNewsletterBlogConfirm(item, section) {
      return new Promise((resolve) => {
        this.newsletterBlogConfirmResolver = resolve;
        this.pendingNewsletterConfirmItem = item;
        this.pendingNewsletterConfirmSection = section;
        this.newsletterBlogConfirmDialog = true;
      });
    },
    async resolveNewsletterBlogConfirm(wantsBlog) {
      if (!wantsBlog) {
        this.newsletterBlogConfirmDialog = false;
        if (this.newsletterBlogConfirmResolver) {
          this.newsletterBlogConfirmResolver(false);
          this.newsletterBlogConfirmResolver = null;
        }
        this.pendingNewsletterConfirmItem = null;
        this.pendingNewsletterConfirmSection = null;
        return;
      }

      if (!this.pendingNewsletterConfirmItem || !this.pendingNewsletterConfirmSection) {
        this.showToast('Failed to prepare blog content', 'error');
        this.newsletterBlogConfirmDialog = false;
        if (this.newsletterBlogConfirmResolver) {
          this.newsletterBlogConfirmResolver(false);
          this.newsletterBlogConfirmResolver = null;
        }
        return;
      }

      this.newsletterConfirmLoading = true;

      try {
        const item = this.pendingNewsletterConfirmItem;
        const section = this.pendingNewsletterConfirmSection;

        await this.performSaveItem(item, section, { forceInactive: true });

        await this.openCreateBlogFromNewsletter(item);

        this.pendingNewsletterSave = { item, section };

        if (this.newsletterBlogConfirmResolver) {
          this.newsletterBlogConfirmResolver(true);
          this.newsletterBlogConfirmResolver = null;
        }
      } catch (err) {
        console.log(err);
        this.showToast(err?.response?.data?.message || 'Failed to prepare blog content', 'error');
        if (this.newsletterBlogConfirmResolver) {
          this.newsletterBlogConfirmResolver(false);
          this.newsletterBlogConfirmResolver = null;
        }
      } finally {
        this.newsletterConfirmLoading = false;
        this.newsletterBlogConfirmDialog = false;
        this.pendingNewsletterConfirmItem = null;
        this.pendingNewsletterConfirmSection = null;
      }
    },
    async openCreateBlogFromNewsletter(item) {
      this.blogDialogMode = 'create';

      let sections = [{ label: "Content", content: "" }];

      if (item.pdf) {
        const res = await axios.post(
          'landing-page-extract-pdf',
          { item_id: item.id },
          { headers: auth() }
        );

        const payload = res?.data?.data ? res.data.data : res?.data;
        const extractedText = payload?.text;

        if (extractedText) {
          const parsedSections = this.splitExtractedTextIntoSections(extractedText);
          if (parsedSections.length) {
            sections = parsedSections;
          }
        }
      }

      this.pendingNewsletterImagePath = item.image || null;

      this.blogDialogInitialData = {
        id: null,
        title: item.title || "",
        text: item.text || "",
        imageUrl: item.image ? `${this.baseURL}/${item.image}` : null,
        sections
      };

      this.blogDialogOpen = true;
      if (item.pdf) {
        this.$nextTick(() => {
          this.proofreadWarningDialog = true;
        });
      }
    },
    splitExtractedTextIntoSections(rawText) {
      if (!rawText) return [];

      const parts = rawText.split(/HALAMAN\s+\d+/i);
      const pages = parts.map(p => p.trim()).filter(p => p.length > 0);

      if (!pages.length) {
        return [{ label: "Content", content: this.plainMarkupToHtml(rawText) }];
      }

      return pages.map((pageText, index) => {
        const boldMatch = pageText.match(/\*\*(.+?)\*\*/);
        const label = boldMatch
          ? boldMatch[1].trim()
          : `Section ${index + 1}`;

        return {
          label,
          content: this.plainMarkupToHtml(pageText)
        };
      });
    },
    createBlog() {
      this.blogDialogMode = 'create';
      this.blogDialogInitialData = {};
      this.blogDialogOpen = true;
    },
    editBlog(item) {
      this.blogDialogMode = 'edit';
      this.blogDialogInitialData = {
        id: item.id,
        title: item.title || "",
        text: item.text || "",
        imageUrl: item.image ? `${this.baseURL}/${item.image}` : null,
        sections: this.plainToBlogSections(item.pdf_content)
      };
      this.blogDialogOpen = true;
    },
    handleBlogDialogClose() {
      this.blogDialogInitialData = {};
      this.pendingNewsletterSave = null;
      this.pendingNewsletterImagePath = null;
    },
    async handleBlogSave(payload) {
      if (this.blogDialogMode === 'edit') {
        await this.saveEditBlog(payload);
      } else {
        await this.saveCreateBlog(payload);
      }

      if (!this.blogDialogOpen && this.pendingNewsletterSave) {
        try {
          await this.activatePendingNewsletter();
          this.showToast('Newsletter and blog successfully saved');
        } catch (err) {
          this.showToast('Blog saved, but failed to activate newsletter', 'error');
        }
        this.pendingNewsletterSave = null;
        this.pendingNewsletterImagePath = null;
      }
    },
    async activatePendingNewsletter() {
      const { item } = this.pendingNewsletterSave;

      const fd = new FormData();
      fd.append('is_active', '1');

      const res = await axios.put(
        `landing-page-items/${item.id}`,
        fd,
        { headers: auth() }
      );

      if (res.data.success) {
        item.is_active = 1;
      }
    },
    async saveCreateBlog(payload) {
      const blogSection = this.allSections.find(s => s.section_key === 'blog');
      const blogHeadline = blogSection?.items?.find(i => i.block_item_id === null && i.title === 'BLOG');

      if (!blogHeadline) {
        this.showToast('Block BLOG belum ditambahkan di section ini', 'error');
        return;
      }

      this.creatingBlog = true;

      try {
        const combinedContent = this.combineSections(payload.sections);
        const authorName = localStorage.getItem('username') || '';

        const fd = new FormData();
        fd.append('title', payload.title);
        fd.append('text', payload.text);
        fd.append('pdf_content', combinedContent);
        fd.append('block_item_id', String(blogHeadline.id));
        fd.append('author_name', authorName);
        fd.append('is_active', '1');

        if (payload.imageFile) {
          fd.append('image', payload.imageFile);
        } else if (!payload.imageRemoved && this.pendingNewsletterImagePath) {
          fd.append('existing_image_path', this.pendingNewsletterImagePath);
        }

        const res = await axios.post(
          `landing-page-sections/blog/items`,
          fd,
          { headers: auth() }
        );

        if (res.data.success) {
          if (!this.pendingNewsletterSave) {
            this.showToast('Blog successfully created');
          }
          this.blogDialogOpen = false;
          await this.getSectionAll();
        }
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Failed to create blog', 'error');
      } finally {
        this.creatingBlog = false;
      }
    },
    async saveEditBlog(payload) {
      this.savingEditBlog = true;

      try {
        const combinedContent = this.combineSections(payload.sections);

        const fd = new FormData();
        fd.append('title', payload.title);
        fd.append('text', payload.text);
        fd.append('pdf_content', combinedContent);
        fd.append('is_active', '1');

        if (payload.imageFile) {
          fd.append('image', payload.imageFile);
        }
        if (payload.imageRemoved) {
          fd.append('remove_image', '1');
        }

        const res = await axios.put(
          `landing-page-items/${payload.id}`,
          fd,
          { headers: auth() }
        );

        if (res.data.success) {
          this.showToast('Blog successfully updated');
          this.blogDialogOpen = false;
          await this.getSectionAll();
        }
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Failed to update blog', 'error');
      } finally {
        this.savingEditBlog = false;
      }
    },
    combineSections(sections) {
      return sections
        .map(section => {
          const plainText = this.htmlToPlainMarkup(section.content);
          if (!plainText) return '';
          const label = (section.label || 'Content').trim();
          return `###SECTION:${label}###\n${plainText}`;
        })
        .filter(text => text.length > 0)
        .join('\n\n');
    },
    async handleInlineImageUpload(file, Editor, cursorLocation, resetUploader) {
      try {
        const fd = new FormData();
        fd.append('image', file);

        const res = await axios.post('landing-page-upload-inline-image', fd, { headers: auth() });

        if (res.data.success) {
          const url = `${this.baseURL}/${res.data.data.path}`;
          Editor.insertEmbed(cursorLocation, 'image', url);
          resetUploader();
        } else {
          this.showToast('Gagal upload gambar', 'error');
        }
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Gagal upload gambar', 'error');
      }
    },
    htmlToPlainMarkup(html) {
      if (!html) return '';

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const output = [];
      let buffer = '';

      const pushLine = (line) => {
        const trimmed = line.trim();
        if (trimmed) output.push(trimmed);
      };

      const flushBuffer = () => {
        pushLine(buffer);
        buffer = '';
      };

      const wrap = (text, marks) => {
        if (!text) return text;
        let result = text;
        if (marks.strike) result = `~~${result}~~`;
        if (marks.underline) result = `__${result}__`;
        if (marks.italic) result = `*${result}*`;
        if (marks.bold) result = `**${result}**`;
        return result;
      };

      const getAlign = (node) => {
        const className = node.className || '';
        if (/ql-align-center/.test(className)) return 'center';
        if (/ql-align-right/.test(className)) return 'right';
        if (/ql-align-justify/.test(className)) return 'justify';

        const style = node.getAttribute?.('style') || '';
        const match = style.match(/text-align\s*:\s*(center|right|justify)/i);
        if (match) return match[1].toLowerCase();

        return null;
      };

      const walk = (node, marks) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const raw = node.textContent.replace(/\s+/g, ' ');
          if (raw) buffer += wrap(raw, marks);
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();
        let nextMarks = marks;

        switch (tag) {
          case 'strong': case 'b':
            nextMarks = { ...marks, bold: true }; break;
          case 'em': case 'i':
            nextMarks = { ...marks, italic: true }; break;
          case 'u':
            nextMarks = { ...marks, underline: true }; break;
          case 's': case 'strike': case 'del':
            nextMarks = { ...marks, strike: true }; break;

          case 'br':
            buffer += '\n';
            return;

          case 'img': {
            const src = node.getAttribute('src') || '';
            flushBuffer();
            pushLine(`###IMAGE:${src}###`);
            return;
          }

          case 'a': {
            const href = node.getAttribute('href') || '';
            const label = node.textContent.replace(/\s+/g, ' ').trim();
            buffer += `${wrap(label, marks)} (${href})`;
            return;
          }

          case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6': {
            flushBuffer();
            node.childNodes.forEach(child => walk(child, marks));
            const content = buffer.trim();
            buffer = '';
            if (content) {
              const level = Math.min(3, parseInt(tag[1], 10));
              const align = getAlign(node);
              const prefix = align ? `@ALIGN=${align}@` : '';
              pushLine(`${prefix}${'#'.repeat(level)} ${content}`);
            }
            return;
          }

          case 'li': {
            const isOrdered = node.parentElement?.tagName.toLowerCase() === 'ol';
            flushBuffer();
            node.childNodes.forEach(child => walk(child, marks));
            const content = buffer.trim();
            buffer = '';
            if (content) {
              if (isOrdered) {
                const siblings = Array.from(node.parentElement.children).filter(
                  el => el.tagName.toLowerCase() === 'li'
                );
                const index = siblings.indexOf(node) + 1;
                pushLine(`${index}. ${content}`);
              } else {
                pushLine(`• ${content}`);
              }
            }
            return;
          }

          case 'p': case 'div': {
            flushBuffer();
            node.childNodes.forEach(child => walk(child, marks));
            const content = buffer.trim();
            buffer = '';
            if (content) {
              const align = getAlign(node);
              const prefix = align ? `@ALIGN=${align}@` : '';
              pushLine(`${prefix}${content}`);
            }
            return;
          }

          default:
            break;
        }

        node.childNodes.forEach(child => walk(child, nextMarks));
      };

      doc.body.childNodes.forEach(node => walk(node, {}));
      flushBuffer();

      return output.join('\n');
    },
    plainToBlogSections(pdfContent) {
      if (!pdfContent) return [];

      const parts = pdfContent.split(/###SECTION:(.*?)###/g);
      const sections = [];
      for (let i = 1; i < parts.length; i += 2) {
        const label = (parts[i] || '').trim();
        const plain = (parts[i + 1] || '').trim();
        sections.push({
          label,
          content: this.plainMarkupToHtml(plain)
        });
      }
      return sections;
    },
    plainMarkupToHtml(text) {
      if (!text) return '';

      let raw = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

      raw = raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      raw = raw.replace(/###IMAGE:(.*?)###/g, (match, src) => `\n<p><img src="${src}"></p>\n`);

      const lines = raw.split('\n');
      const htmlParts = [];

      let listBuffer = [];
      let listType = null;

      const flushList = () => {
        if (listBuffer.length === 0) return;
        const items = listBuffer.map(item => `<li>${item}</li>`).join('');
        htmlParts.push(`<${listType}>${items}</${listType}>`);
        listBuffer = [];
        listType = null;
      };

      const applyInline = (str) => {
        let out = str;
        out = out.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        out = out.replace(/__(.*?)__/g, '<u>$1</u>');
        out = out.replace(/~~(.*?)~~/g, '<s>$1</s>');
        out = out.replace(/\*(.*?)\*/g, '<em>$1</em>');
        return out;
      };

      const extractAlign = (trimmed) => {
        const m = trimmed.match(/^@ALIGN=(center|right|justify)@(.*)$/);
        if (m) return { align: m[1], rest: m[2] };
        return { align: null, rest: trimmed };
      };

      for (const rawLine of lines) {
        let trimmed = rawLine.trim();

        if (!trimmed) {
          flushList();
          continue;
        }

        if (/^<(p|img)/i.test(trimmed)) {
          flushList();
          htmlParts.push(trimmed);
          continue;
        }

        const { align, rest } = extractAlign(trimmed);
        trimmed = rest;
        const alignAttr = align ? ` style="text-align:${align}"` : '';

        const numberedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
        const bulletMatch = trimmed.match(/^•\s+(.*)$/);

        if (numberedMatch) {
          if (listType !== 'ol') flushList();
          listType = 'ol';
          listBuffer.push(applyInline(numberedMatch[1]));
          continue;
        }

        if (bulletMatch) {
          if (listType !== 'ul') flushList();
          listType = 'ul';
          listBuffer.push(applyInline(bulletMatch[1]));
          continue;
        }

        flushList();

        if (/^###\s+/.test(trimmed)) {
          htmlParts.push(`<h3${alignAttr}>${applyInline(trimmed.replace(/^###\s+/, ''))}</h3>`);
        } else if (/^##\s+/.test(trimmed)) {
          htmlParts.push(`<h2${alignAttr}>${applyInline(trimmed.replace(/^##\s+/, ''))}</h2>`);
        } else if (/^#\s+/.test(trimmed)) {
          htmlParts.push(`<h1${alignAttr}>${applyInline(trimmed.replace(/^#\s+/, ''))}</h1>`);
        } else {
          htmlParts.push(`<p${alignAttr}>${applyInline(trimmed)}</p>`);
        }
      }

      flushList();

      return htmlParts.join('');
    },
    getDisplayGroups(section) {
      const sorted = [...section.items].sort((a, b) => a.sort_order - b.sort_order);
      const byId = new Map(sorted.map(i => [i.id, i]));

      const childrenMap = new Map();
      for (const item of sorted) {
        const parentId = item.block_item_id;
        if (parentId != null && byId.has(parentId)) {
          if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
          childrenMap.get(parentId).push(item);
        }
      }

      const groups = [];
      let flatBuffer = [];

      const flushFlatBuffer = () => {
        if (flatBuffer.length > 0) {
          groups.push({ headline: null, items: flatBuffer });
          flatBuffer = [];
        }
      };

      for (const item of sorted) {
        const isChild = item.block_item_id != null && byId.has(item.block_item_id);
        if (isChild) continue;

        const children = childrenMap.get(item.id) || [];

        if (children.length > 0) {
          flushFlatBuffer();
          groups.push({
            headline: item,
            items: children.sort((a, b) => a.sort_order - b.sort_order),
            isImageGallery: false,
          });
        } else if (item.item_image && item.item_image > 1) {
          flushFlatBuffer();
          const imageList = this.parseItemImages(item);

          const virtualSlots = Array.from({ length: item.item_image }, (_, idx) => {
            const key = `${item.id}_${idx}`;
            let slot = this.slotCache[key];

            if (!slot) {
              slot = {
                _isImageSlot: true,
                _slotIndex: idx,
                _parentItem: item,
                _pendingKey: key,
                _dragOver: false,
                _saving: false,
                _file: null,
                _removeImage: false,
                image: imageList[idx] || null,
              };
              this.$set(this.slotCache, key, slot);
            } else {
              slot._parentItem = item;
              if (!slot._file && !slot._removeImage) {
                slot.image = imageList[idx] || null;
              }
            }
            return slot;
          });

          groups.push({
            headline: item,
            items: virtualSlots,
            isImageGallery: true,
          });
        } else {
          flatBuffer.push(item);
        }
      }
      flushFlatBuffer();

      return groups;
    },
    parseItemImages(item) {
      if (!item.image) return [];
      try {
        const parsed = JSON.parse(item.image);
        return Array.isArray(parsed) ? parsed : [item.image];
      } catch {
        return [item.image];
      }
    },
    async addItemToGroup(group, section) {
      const guardKey = group.headline?.id ?? group.headline?._key ?? `flat-${section.id}`;
      if (!this.addItemGroupLocks) this.addItemGroupLocks = new Set();
      if (this.addItemGroupLocks.has(guardKey)) return;

      const headlineTitle = group.headline?.title || '';

      if (headlineTitle === 'BLOG') {
        this.createBlog();
        return;
      }

      this.addItemGroupLocks.add(guardKey);

      try {
        if (group.isImageGallery && group.headline) {
          const parent = group.headline;
          try {
            const fd = new FormData();
            fd.append('add_slot', '1');

            const res = await axios.put(`landing-page-items/${parent.id}`, fd, { headers: auth() });

            if (res.data.success) {
              parent.image = res.data.data.image;
              parent.item_image = res.data.data.item_image;
              this.showToast('Item added');
            }
          } catch (err) {
            this.showToast(err?.response?.data?.message || 'Failed to add item', 'error');
          }
          return;
        }

        const parentId = group.headline?.id ?? null;

        let newItemTemplate = {};

        if (headlineTitle === 'NEWSLETTER') {
          newItemTemplate = { icon: null, title: 'Newsletter Title', role: null, text: 'Insight mendalam tentang', image: null, pdf: null };
        } else if (headlineTitle === 'TEAM' || headlineTitle === 'OUR TEAMS' || group.items.some(i => i.role !== null)) {
          newItemTemplate = { icon: null, title: 'Intan Hartri Putri', role: 'Product Owner', text: '', image: null, pdf: null };
        } else if (headlineTitle === 'FREQUENTLY ASKED QUESTIONS' || headlineTitle === 'FAQ') {
          newItemTemplate = { icon: null, title: 'Who should use MedAn247?', role: null, text: 'MedAn247 is ideal for marketing teams, communications professionals, government agencies, and any organization that needs to monitor public digital conversations.', image: null, pdf: null };
        } else if (headlineTitle === 'TECHNOLOGY') {
          newItemTemplate = { icon: 'mdi-chart-line', title: headlineTitle, role: null, text: 'A fast, stable, and scalable system for real-time data monitoring.', image: null, pdf: null };
        } else if (headlineTitle === 'PRODUCT OVERVIEW') {
          newItemTemplate = { icon: 'mdi-star-outline', title: null, role: null, text: '', image: null, pdf: null };
        } else if (headlineTitle === 'WHY CHOOSE US') {
          newItemTemplate = { icon: 'mdi-calendar-blank-outline', title: 'Powerful Integration', role: null, text: 'Our product seamlessly integrates with on premises and private cloud data sources, providing secure and easy access to your data in one platform.', image: null, pdf: null };
        } else if (headlineTitle === 'FEATURES') {
          newItemTemplate = { icon: 'mdi-calendar-blank-outline', title: null, role: null, text: 'Real time monitoring across social media, news portals, forums, and other digital channels.', image: null, pdf: null };
        } else if (!group.headline && group.items.some(i => i.title === 'Information Overload')) {
          newItemTemplate = { icon: 'mdi-calendar-month-outline', title: 'Information Overload', role: null, text: '', image: null, pdf: null };
        } else {
          newItemTemplate = { icon: null, title: '', role: null, text: '', image: null, pdf: null };
        }

        const lastItem = group.items.length > 0
          ? group.items[group.items.length - 1]
          : group.headline;
        const newSortOrder = lastItem ? (lastItem.sort_order + 1) : (section.items.length + 1);

        const newItem = {
          id: null,
          section_id: section.id,
          block_item_id: parentId,
          ...newItemTemplate,
          sort_order: newSortOrder,
          is_active: 1,
          _saving: false,
          _key: nextKey(),
          _isNew: true,
          _pickerOpen: false,
          _dragOver: false,
          _dragOverPdf: false,
          _removePdf: false,
          _removeImage: false,
          _hasTitleField: newItemTemplate.title !== null && newItemTemplate.title !== undefined && newItemTemplate.title !== '',
          _hasTextField: newItemTemplate.text !== null && newItemTemplate.text !== undefined && newItemTemplate.text !== '',
          _hasIconField: newItemTemplate.icon !== null && newItemTemplate.icon !== undefined && newItemTemplate.icon !== '',
          _hasRoleField: newItemTemplate.role !== null && newItemTemplate.role !== undefined,
          _isFeatureOnly: (newItemTemplate.title === null || newItemTemplate.title === undefined || newItemTemplate.title === '')
            ? true
            : !!(newItemTemplate.icon && newItemTemplate.text && group.headline && newItemTemplate.title === group.headline.title),
          _isNewsletterChild: group.headline?.title === 'NEWSLETTER',
        };

        section.items.push(newItem);

        try {
          const fd = new FormData();
          if (newItem.icon) fd.append('icon', newItem.icon);
          if (newItem.title !== null && newItem.title !== undefined) fd.append('title', newItem.title);
          if (newItem.role) fd.append('role', newItem.role);
          fd.append('text', newItem.text || '');
          fd.append('sort_order', String(newItem.sort_order));
          fd.append('is_active', '1');
          if (parentId) fd.append('block_item_id', String(parentId));

          const res = await axios.post(
            `landing-page-sections/${section.section_key}/items`,
            fd,
            { headers: auth() }
          );

          if (res.data.success) {
            newItem.id = res.data.data.id;
            newItem._isNew = false;
            newItem.icon = res.data.data.icon;
            newItem.title = res.data.data.title;
            newItem.role = res.data.data.role;
            newItem.text = res.data.data.text;
            newItem.block_item_id = res.data.data.block_item_id;
            this.showToast('Item added');
          }
        } catch (err) {
          section.items = section.items.filter(i => i._key !== newItem._key);
          this.showToast(err?.response?.data?.message || 'Failed to add item', 'error');
        }
      } finally {
        this.addItemGroupLocks.delete(guardKey);
      }
    },
    confirmDelete(target) {
      this.deleteTarget = target;
      this.dialogDelete = true;
      return new Promise((resolve) => {
        this.deleteResolver = resolve;
      });
    },
    deleteItemConfirm() {
      this.dialogDelete = false;
      if (this.deleteResolver) this.deleteResolver(true);
      this.deleteResolver = null;
      this.deleteTarget = null;
    },
    closeDelete() {
      this.dialogDelete = false;
      if (this.deleteResolver) this.deleteResolver(false);
      this.deleteResolver = null;
      this.deleteTarget = null;
    },
    async deleteBlock(group, section) {
      if (this.deletingBlock) return;

      const allItems = [
        ...(group.headline ? [group.headline] : []),
        ...group.items,
      ];

      const label = group.headline?.title
        || (section.subtitle && section.subtitle.trim() !== '' ? section.subtitle : null)
        || section.nav_label
        || section.section_name
        || section.section_key
        || 'block';

      const confirmed = await this.confirmDelete({ type: 'block', label, count: allItems.length });
      if (!confirmed) return;

      this.deletingBlock = true;

      try {
        let allSucceeded = true;

        for (const item of allItems) {
          if (!item.id) {
            const idx = section.items.indexOf(item);
            if (idx !== -1) section.items.splice(idx, 1);
            continue;
          }

          try {
            const fd = new FormData();
            fd.append('is_active', '0');
            await axios.put(`landing-page-items/${item.id}`, fd, { headers: auth() });

            item.is_active = 0;
            const idx = section.items.indexOf(item);
            if (idx !== -1) section.items.splice(idx, 1);
          } catch (err) {
            allSucceeded = false;
            console.error(`Failed to deactivate item "${item.title || item.text}":`, err?.response?.data?.message || err.message);
          }
        }

        if (!allSucceeded) {
          this.showToast(`Some items in block "${label}" failed to delete`, 'error');
        } else {
          this.showToast(`Block "${label}" deleted`);
        }

        const existingFlagsById = new Map();
        section.items.forEach(i => {
          if (i.id) {
            existingFlagsById.set(i.id, {
              _hasTitleField: i._hasTitleField,
              _hasRoleField: i._hasRoleField,
              _hasTextField: i._hasTextField,
              _hasIconField: i._hasIconField,
              _isNewsletterChild: i._isNewsletterChild,
              _isFeatureOnly: i._isFeatureOnly,
            });
          }
        });

        try {
          const ir = await axios.get(`landing-page-sections/${section.section_key}/items`, { headers: auth() });
          if (ir.data.success) {
            const rawItems = ir.data.data;
            const rawById = new Map(rawItems.map(i => [i.id, i]));

            section.items = rawItems.map(i => {
              const prev = existingFlagsById.get(i.id);
              const rawParent = i.block_item_id != null ? rawById.get(i.block_item_id) : null;

              const computedFeatureOnly = (i.title === null || i.title === undefined || i.title === '')
                ? true
                : !!(i.icon && i.icon !== '' && i.text && rawParent && i.title === rawParent.title);

              return {
                ...i,
                _key: nextKey(),
                _saving: false,
                _pickerOpen: false,
                _dragOverPdf: false,
                pdf: i.pdf ?? null,
                _hasTitleField: prev ? prev._hasTitleField : (i.title !== null && i.title !== undefined && i.title !== ''),
                _hasRoleField: prev ? prev._hasRoleField : (i.role !== null && i.role !== undefined && i.role !== ''),
                _hasTextField: prev ? prev._hasTextField : (i.text !== null && i.text !== undefined && i.text !== ''),
                _hasIconField: prev ? prev._hasIconField : (i.icon !== null && i.icon !== undefined && i.icon !== ''),
                _isNewsletterChild: prev ? prev._isNewsletterChild : (rawParent?.title === 'NEWSLETTER'),
                _isFeatureOnly: prev ? prev._isFeatureOnly : computedFeatureOnly,
              };
            });
          }
        } catch (err) {
          console.error('Failed to refresh section items:', err?.response?.data?.message || err.message);
        }

        try {
          const tr = await axios.get('landing-page-sections-templates', { headers: auth() });
          this.templateSections = tr.data.success ? tr.data.data : this.templateSections;
        } catch (err) {
          console.error('Failed to refresh templates:', err?.response?.data?.message || err.message);
        }

        this.buildBlockTemplates();
      } finally {
        this.deletingBlock = false;
      }
    },
    isNewsletterItem(item) {
      return item._isNewsletterChild === true;
    },
    onPdfChange(e, item) {
      const file = e.target.files[0];
      if (!file) return;

      const fileExtension = file.name.toLowerCase().split('.').pop();

      if (fileExtension !== 'pdf') {
        this.dialogWrongExtensionNewsletter = true;
        e.target.value = null;
        return;
      }

      item._pdfFile = file;
      item.pdf = file.name;
    },
    handleDropPdf(e, item) {
      item._dragOverPdf = false;
      const file = e.dataTransfer.files[0];
      if (!file) return;

      const fileExtension = file.name.toLowerCase().split('.').pop();

      if (fileExtension !== 'pdf') {
        this.dialogWrongExtensionNewsletter = true;
        return;
      }

      item._pdfFile = file;
      item.pdf = file.name;
    },
    removePdf(item) {
      item._pdfFile = null;
      item.pdf = null;
      item._removePdf = true;
      this.skipBlogPromptKeys.add(this.getItemKey(item));
    },
    getItemKey(item) {
      return item.id ?? item._key;
    },
    buildBlockTemplates() {
      const templates = [];

      const headlineWithImage = ['PRODUCT OVERVIEW', 'TECHNOLOGY'];
      this.headlineImageTitles = new Set(headlineWithImage);

      const allSourceSections = [
        ...this.allSections,
        ...this.templateSections.map(ts => {
          const existing = this.allSections.find(s => s.section_key === ts.section_key);
          if (existing) return null;
          return ts;
        }).filter(Boolean)
      ];

      const mergedSections = allSourceSections.map(s => {
        const templateSection = this.templateSections.find(ts => ts.section_key === s.section_key);
        const templateItems = templateSection ? templateSection.items : [];
        return {
          ...s,
          items: [...(s.items || []), ...templateItems]
        }
      });

      mergedSections.forEach(section => {
        if (!section.items || !section.items.length) return;

        const byId = new Map(section.items.map(i => [i.id, i]));
        const childrenMap = new Map();
        const roots = [];

        const sorted = [...section.items].sort((a, b) => a.sort_order - b.sort_order);

        for (const item of sorted) {
          const parentId = item.block_item_id;
          const parentExists = parentId != null && byId.has(parentId);

          if (parentExists) {
            if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
            childrenMap.get(parentId).push(item);
          } else {
            roots.push(item);
          }
        }

        const headlineRoots = roots.filter(r => childrenMap.has(r.id));
        const flatRoots = roots.filter(r => !childrenMap.has(r.id))
          .sort((a, b) => a.sort_order - b.sort_order);

        const groups = headlineRoots.map(headline => ({
          headline,
          items: (childrenMap.get(headline.id) || []).sort((a, b) => a.sort_order - b.sort_order),
        }));

        if (flatRoots.length > 0) {
          if (headlineRoots.length === 0) {
            groups.push({ headline: null, items: flatRoots });
          } else {
            const isUppercaseHeading = (item) =>
              item.title && item.title.trim() !== '' && item.title === item.title.toUpperCase();

            const standaloneFlats = flatRoots.filter(isUppercaseHeading);
            const groupedFlats = flatRoots.filter(i => !isUppercaseHeading(i));

            standaloneFlats.forEach(item => {
              groups.push({ headline: null, items: [item] });
            });

            if (groupedFlats.length > 0) {
              groups.push({ headline: null, items: groupedFlats });
            }
          }
        }

        groups.forEach((group, gIdx) => {
          const allItems = group.headline ? [group.headline, ...group.items] : group.items;
          if (!allItems.length) return;

          const headlineTitle = group.headline?.title || '';
          const flatLabel = !group.headline && group.items.length === 1
            ? (group.items[0].title || group.items[0].text)
            : null;

          const label = headlineTitle
            ? `Block — ${headlineTitle}`
            : flatLabel
              ? `Block — ${flatLabel}`
              : section.subtitle && section.subtitle.trim() !== ''
                ? `Block — ${section.subtitle}`
                : `Block — ${section.nav_label || section.section_name || section.section_key}`;

          const icon = group.headline
            ? 'mdi-view-headline'
            : (allItems[0]?.icon && allItems[0].icon !== '' ? allItems[0].icon : 'mdi-view-list-outline');

          const preview = allItems.slice(0, 5).map((i) => ({
            icon: i.icon && i.icon !== '' ? i.icon : (i.role ? 'mdi-account-outline' : 'mdi-text'),
            text: i.title && i.title !== ''
              ? i.title
              : (i.text ? (i.text.length > 40 ? i.text.substring(0, 40) + '…' : i.text) : `(${i.role || 'item'})`),
            itemImage: i.item_image ?? 0,
          }));

          const items = allItems.map(i => ({
            id: i.id ?? null,
            source_section_id: section.id,
            block_item_id: i.block_item_id ?? null,
            is_active: i.is_active,
            icon: i.icon ?? '',
            title: i.title ?? '',
            role: i.role ?? null,
            text: i.text ?? '',
            image: i.image ?? null,
            item_image: i.item_image ?? null,
            pdf: i.pdf ?? null,
          }));

          templates.push({
            id: `${section.section_key}_${gIdx}`,
            source_section_id: section.id,
            source_section_key: section.section_key,
            label,
            icon,
            preview,
            items,
            headlineTitle,
          });
        });
      });

      const seen = new Set();
      this.blockTemplates = templates.filter(t => {
        const headlineItemImage = t.items.find(i => i.block_item_id === null)?.item_image ?? null;
        const dedupKey = `${t.label}__${headlineItemImage}`;
        if (seen.has(dedupKey)) return false;
        seen.add(dedupKey);
        return true;
      });
    },
    openBlockPicker(section) {
      this.blockPicker.show = true;
      this.blockPicker.section = section;
      this.blockPicker.selectedId = null;
    },
    closeBlockPicker() {
      this.blockPicker.show = false;
      this.blockPicker.section = null;
      this.blockPicker.selectedId = null;
    },
    async addBlockItems() {
      if (this.addingBlock) return;

      const template = this.blockTemplates.find(b => b.id === this.blockPicker.selectedId);
      const section = this.blockPicker.section;
      if (!template || !section) return;

      this.addingBlock = true;

      try {
        const headlineTpl = template.items.find(i =>
          i.title && i.title === i.title.toUpperCase() && (i.icon === null || i.icon === '')
        );

        if (headlineTpl) {
          const alreadyActive = section.items.some(i => i.block_item_id === null && i.is_active === 1 && i.title === headlineTpl.title);
          if (alreadyActive) {
            this.showToast(`Block "${template.label}" sudah ada di section ini`, 'error');
            this.closeBlockPicker();
            return;
          }
        } else {
          const templateTitles = template.items.map(i => i.title).filter(Boolean).sort();
          const sectionRootTitles = section.items
            .filter(i => i.block_item_id === null && i.is_active === 1)
            .map(i => i.title)
            .filter(Boolean)
            .sort();

          const allAlreadyActive = templateTitles.length > 0 &&
            templateTitles.every(t => sectionRootTitles.includes(t));

          if (allAlreadyActive) {
            this.showToast(`Block "${template.label}" sudah ada di section ini`, 'error');
            this.closeBlockPicker();
            return;
          }
        }

        let successCount = 0;
        let failCount = 0;
        let savedHeadlineId = null;

        const orderedItems = headlineTpl
          ? [headlineTpl, ...template.items.filter(i => i !== headlineTpl)]
          : template.items;

        for (const tpl of orderedItems) {
          const isHeadline = tpl === headlineTpl;
          const nextSortOrder = section.items.length + 1;
          const canReactivate = tpl.id && tpl.source_section_id === section.id;
          const imageHeadlines = ['PRODUCT OVERVIEW', 'TECHNOLOGY'];
          const defaultItemImage = tpl.item_image ?? (isHeadline && imageHeadlines.includes(tpl.title) ? 1 : null);

          try {
            if (canReactivate) {
              const fd = new FormData();
              fd.append('is_active', '1');
              fd.append('sort_order', String(nextSortOrder));
              if (defaultItemImage !== null && (tpl.item_image === null || tpl.item_image === undefined)) {
                fd.append('item_image', String(defaultItemImage));
              }
              if (isHeadline) {
                fd.append('block_item_id', '');
              } else if (savedHeadlineId) {
                fd.append('block_item_id', String(savedHeadlineId));
              }

              const res = await axios.put(`landing-page-items/${tpl.id}`, fd, { headers: auth() });

              if (res.data.success) {
                const reactivated = {
                  ...res.data.data,
                  section_id: section.id,
                  _saving: false,
                  _key: nextKey(),
                  _isNew: false,
                  _pickerOpen: false,
                  _dragOver: false,
                  _dragOverPdf: false,
                  _removePdf: false,
                  _removeImage: false,
                  pdf: res.data.data.pdf ?? null,
                  item_image: res.data.data.item_image ?? defaultItemImage,
                  _hasTitleField: res.data.data.title !== null && res.data.data.title !== undefined && res.data.data.title !== '',
                  _hasTextField: res.data.data.text !== null && res.data.data.text !== undefined && res.data.data.text !== '',
                  _hasIconField: res.data.data.icon !== null && res.data.data.icon !== undefined && res.data.data.icon !== '',
                  _hasRoleField: res.data.data.role !== null && res.data.data.role !== undefined,
                  _isFeatureOnly: (res.data.data.title === null || res.data.data.title === undefined || res.data.data.title === '')
                    ? true
                    : !!(res.data.data.icon && res.data.data.text && headlineTpl && res.data.data.title === headlineTpl.title),
                  _isNewsletterChild: isHeadline ? false : (headlineTpl?.title === 'NEWSLETTER'),
                };
                section.items.push(reactivated);

                if (isHeadline) savedHeadlineId = reactivated.id;
                successCount++;
              } else {
                failCount++;
              }
            } else {
              const newItem = {
                id: null,
                section_id: section.id,
                block_item_id: isHeadline ? null : savedHeadlineId,
                icon: tpl.icon,
                title: tpl.title,
                role: tpl.role,
                text: tpl.text,
                image: null,
                item_image: defaultItemImage,
                pdf: null,
                sort_order: nextSortOrder,
                is_active: 1,
                _saving: false,
                _key: nextKey(),
                _isNew: true,
                _pickerOpen: false,
                _dragOver: false,
                _dragOverPdf: false,
                _removePdf: false,
                _removeImage: false,
                _hasTitleField: tpl.title !== null && tpl.title !== undefined && tpl.title !== '',
                _hasTextField: tpl.text !== null && tpl.text !== undefined && tpl.text !== '',
                _hasIconField: tpl.icon !== null && tpl.icon !== undefined && tpl.icon !== '',
                _hasRoleField: tpl.role !== null && tpl.role !== undefined,
                _isFeatureOnly: (tpl.title === null || tpl.title === undefined || tpl.title === '')
                  ? true
                  : !!(tpl.icon && tpl.text && headlineTpl && tpl.title === headlineTpl.title),
                _isNewsletterChild: isHeadline ? false : (headlineTpl?.title === 'NEWSLETTER'),
              };

              section.items.push(newItem);

              try {
                const fd = new FormData();
                if (newItem.icon) fd.append('icon', newItem.icon);
                if (newItem.title) fd.append('title', newItem.title);
                if (newItem.role) fd.append('role', newItem.role);
                fd.append('text', newItem.text || '');
                fd.append('sort_order', String(newItem.sort_order));
                fd.append('is_active', '1');
                if (defaultItemImage !== null) fd.append('item_image', String(defaultItemImage));
                if (newItem.block_item_id) fd.append('block_item_id', String(newItem.block_item_id));

                const res = await axios.post(
                  `landing-page-sections/${section.section_key}/items`,
                  fd,
                  { headers: auth() }
                );

                if (res.data.success) {
                  newItem.id = res.data.data.id;
                  newItem._isNew = false;
                  newItem.icon = res.data.data.icon;
                  newItem.title = res.data.data.title;
                  newItem.role = res.data.data.role;
                  newItem.text = res.data.data.text;
                  newItem.block_item_id = res.data.data.block_item_id;
                  newItem.item_image = res.data.data.item_image ?? defaultItemImage;

                  if (isHeadline) savedHeadlineId = newItem.id;
                  successCount++;
                } else {
                  const idx = section.items.indexOf(newItem);
                  if (idx !== -1) section.items.splice(idx, 1);
                  failCount++;
                }
              } catch (err) {
                const idx = section.items.indexOf(newItem);
                if (idx !== -1) section.items.splice(idx, 1);
                console.error(`Failed to add block item "${tpl.title || tpl.text}":`,
                  err?.response?.data?.message || err.message
                );
                failCount++;
              }
            }
          } catch (err) {
            console.error(`Failed to add block item "${tpl.title || tpl.text}":`,
              err?.response?.data?.message || err.message
            );
            failCount++;
          }
        }

        if (failCount === 0) {
          this.showToast(`Block "${template.label}" added — ${successCount} item${successCount > 1 ? 's' : ''} saved`);
        } else {
          this.showToast(`Block added — ${successCount} saved, ${failCount} failed`, 'error');
        }

        this.closeBlockPicker();

        if (headlineTpl?.title === 'BLOG') {
          this.createBlog();
        }

        try {
          const tr = await axios.get('landing-page-sections-templates', { headers: auth() });
          this.templateSections = tr.data.success ? tr.data.data : this.templateSections;
          this.buildBlockTemplates();
        } catch (err) {
          console.error('Failed to refresh templates:', err?.response?.data?.message || err.message);
        }
      } finally {
        this.addingBlock = false;
      }
    },
    isHeadlineItem(item) {
      if (item.block_item_id !== null) return false;

      return this.headlineImageTitles.has(item.title);
    },
    showTitleInput(item, group) {
      if (!item._hasTitleField) return false;
      if (!this.isFeatureOnlyItem(item)) return true;
      if (group?.headline?.title === 'TECHNOLOGY') return true;
      return !!item.title;
    },
    isFeatureOnlyItem(item) {
      return item._isFeatureOnly === true;
    },
    isSocialOrLink(item) {
      const linkKeys = [
        'COMPANY', 'SUPPORT', 'instagram', 'dribbble', 'twitter', 'youtube',
        'fa-brands fa-instagram', 'fa-brands fa-dribbble', 'fa-brands fa-x-twitter', 'fa-brands fa-youtube'
      ];
      return linkKeys.includes(item.icon) || (item.text && item.text.startsWith('#'));
    },
    async getSectionAll() {
      this.loading = true;
      this.error = null;

      const existingFlagsById = new Map();
      (this.allSections || []).forEach(s => {
        (s.items || []).forEach(i => {
          if (i.id) {
            existingFlagsById.set(i.id, {
              _hasTitleField: i._hasTitleField,
              _hasRoleField: i._hasRoleField,
              _hasTextField: i._hasTextField,
              _hasIconField: i._hasIconField,
              _isFeatureOnly: i._isFeatureOnly,
              _isNewsletterChild: i._isNewsletterChild,
            });
          }
        });
      });

      try {
        const res = await axios.get('landing-page-sections/all', { headers: auth() });
        if (!res.data.success) throw new Error('Failed to load sections');

        const sectionsWithItems = await Promise.all(
          res.data.data.map(async (s) => {
            const ir = await axios.get(`landing-page-sections/${s.section_key}/items`, { headers: auth() });
            const rawItems = ir.data.success ? ir.data.data : [];

            const rawById = new Map(rawItems.map(i => [i.id, i]));

            const items = rawItems.map(i => {
              const prev = existingFlagsById.get(i.id);
              const rawParent = i.block_item_id != null ? rawById.get(i.block_item_id) : null;
              const computedFeatureOnly = i.title === null || i.title === undefined
                ? true
                : !!(i.icon && i.icon !== '' && i.text && rawParent && i.title === rawParent.title);

              return {
                ...i,
                _key: nextKey(),
                _saving: false,
                _pickerOpen: false,
                _dragOverPdf: false,
                pdf: i.pdf ?? null,
                _hasTitleField: prev ? prev._hasTitleField : (i.title !== null && i.title !== undefined && i.title !== ''),
                _hasRoleField: prev ? prev._hasRoleField : (i.role !== null && i.role !== undefined && i.role !== ''),
                _hasTextField: prev ? prev._hasTextField : (i.text !== null && i.text !== undefined && i.text !== ''),
                _hasIconField: prev ? prev._hasIconField : (i.icon !== null && i.icon !== undefined && i.icon !== ''),
                _isNewsletterChild: prev ? prev._isNewsletterChild : (rawParent?.title === 'NEWSLETTER'),
                _isFeatureOnly: prev ? prev._isFeatureOnly : computedFeatureOnly,
              };
            });

            return {
              ...s,
              _saving: false,
              _key: nextKey(),
              section_key_draft: s.section_key,
              items,
            };
          })
        );
        this.allSections = sectionsWithItems.sort((a, b) => a.sort_order - b.sort_order);

        const tr = await axios.get('landing-page-sections-templates', { headers: auth() });
        this.templateSections = tr.data.success ? tr.data.data : [];

        this.buildBlockTemplates();
      } catch (e) {
        this.error = e?.response?.data?.message || e.message;

        if (e.response) {
          const { status } = e.response;

          switch (status) {
            case 404:
              this.$refs.alert.error_login(404);
              break;
            case 403:
              this.$refs.alert.error_login(403);
              break;
            default:
              console.log("some other error");
              break;
          }
        } else {
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 10000);
          this.message = "API Landing Sections Refused To Connect";
          this.colorAlert = "red";
        }
      } finally {
        this.loading = false;
      }
    },
    setActive(key) {
      this.activeKey = this.activeKey === key ? null : key;
    },
    autoGenerateKey(section) {
      if (section.section_name) {
        section.section_key_draft = section.section_name
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_]/g, '');
      }
    },
    async saveSection(section, toastMessage = null) {
      section._saving = true;
      try {
        if (section._isNew) {
          this.autoGenerateKey(section);
          const newKey = section.section_key_draft || section.section_key;

          const res = await axios.post(
            `landing-page-sections`,
            {
              section_key: newKey,
              section_name: section.section_name,
              nav_label: section.nav_label,
              subtitle: section.subtitle,
              title: section.title,
              description: section.description,
              icon: section.icon,
              sort_order: section.sort_order,
              is_active: section.is_active,
            },
            { headers: auth() }
          );
          if (res.data.success) {
            section.id = res.data.data.id;
            section._isNew = false;
            section.section_key = res.data.data.section_key;
            section.section_key_draft = res.data.data.section_key;
          }
        } else {
          const oldKey = section.section_key;
          this.autoGenerateKey(section);
          const newKey = section.section_key_draft || oldKey;

          await axios.put(
            `landing-page-sections/${oldKey}`,
            {
              section_name: section.section_name,
              section_key: newKey,
              subtitle: section.subtitle,
              title: section.title,
              description: section.description,
              nav_label: section.nav_label,
              icon: section.icon,
              sort_order: section.sort_order,
              is_active: section.is_active,
            },
            { headers: auth() }
          );

          if (newKey !== oldKey) {
            section.section_key = newKey;
            section.section_key_draft = newKey;
          }
        }
        this.showToast(toastMessage || `"${section.section_key}" saved`);
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Failed to save section', 'error');
      } finally {
        section._saving = false;
      }
    },
    async hideSection(section) {
      section.is_active = 0;
      await this.saveSection(section, `Section "${section.section_key}" hidden`);
      if (this.activeKey === section.section_key) this.activeKey = null;
    },
    async restoreSection(section) {
      section.is_active = 1;
      await this.saveSection(section, `Section "${section.section_key}" restored to canvas`);
    },
    async deleteSection(section) {
      if (this.pendingActions?.has(`deleteSection_${section.section_key}`)) return;
      if (!this.pendingActions) this.pendingActions = new Set();

      const confirmed = await this.confirmDelete({ type: 'section', label: section.section_key });
      if (!confirmed) return;

      this.pendingActions.add(`deleteSection_${section.section_key}`);
      try {
        await axios.put(
          `landing-page-sections/${section.section_key}`,
          { is_active: 0 },
          { headers: auth() }
        );
        const idx = this.allSections.findIndex(s => s.section_key === section.section_key);
        if (idx !== -1) this.allSections.splice(idx, 1);
        if (this.activeKey === section.section_key) this.activeKey = null;
        this.showToast(`"${section.section_key}" removed`);
      } catch {
        this.showToast('Failed to delete section', 'error');
      } finally {
        this.pendingActions.delete(`deleteSection_${section.section_key}`);
      }
    },
    async addNewSection(block) {
      const exists = this.allSections.find(s => s.section_key === block.type);
      if (exists) {
        if (!exists.is_active) {
          await this.restoreSection(exists);
        } else {
          this.showToast(`Section "${block.type}" already on canvas`, 'error');
        }
        return null;
      }

      const newKey = block.type === 'custom' ? `custom_${Date.now()}` : block.type;

      const newSection = {
        section_key: newKey,
        section_name: block.label,
        nav_label: block.label,
        icon: block.icon || 'mdi-view-grid-outline',
        subtitle: '',
        title: `New ${block.label} Section`,
        description: '',
        is_active: 1,
        sort_order: this.allSections.length + 1,
        _saving: false,
        _key: nextKey(),
        items: [],
        _isNew: true,
      };

      this.allSections.push(newSection);
      this.activeKey = newKey;

      try {
        const res = await axios.post(
          'landing-page-sections',
          {
            section_key: newSection.section_key,
            section_name: newSection.section_name,
            nav_label: newSection.nav_label,
            icon: newSection.icon,
            subtitle: newSection.subtitle,
            title: newSection.title,
            description: newSection.description,
            sort_order: newSection.sort_order,
            is_active: newSection.is_active,
          },
          { headers: auth() }
        );
        if (res.data.success) {
          newSection.id = res.data.data.id;
          newSection._isNew = false;
          this.showToast(`Section "${block.label}" added`);
        }
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Failed to create section', 'error');
      }

      return newSection;
    },
    async saveAll() {
      this.saving = true;
      try {
        await Promise.all(
          this.allSections.map(async (s, idx) => {
            s.sort_order = idx + 1;
            try {
              if (s._isNew) {
                this.autoGenerateKey(s);
                const newKey = s.section_key_draft || s.section_key;

                const res = await axios.post(
                  `landing-page-sections`,
                  {
                    section_key: newKey,
                    section_name: s.section_name,
                    nav_label: s.nav_label,
                    subtitle: s.subtitle,
                    title: s.title,
                    description: s.description,
                    icon: s.icon,
                    sort_order: s.sort_order,
                    is_active: s.is_active,
                  },
                  { headers: auth() }
                );
                if (res.data.success) {
                  s.id = res.data.data.id;
                  s._isNew = false;
                  s.section_key = res.data.data.section_key;
                  s.section_key_draft = res.data.data.section_key;
                }
              } else {
                const oldKey = s.section_key;
                this.autoGenerateKey(s);
                const newKey = s.section_key_draft || oldKey;

                await axios.put(
                  `landing-page-sections/${s.section_key}`,
                  {
                    section_name: s.section_name,
                    section_key: newKey,
                    subtitle: s.subtitle,
                    title: s.title,
                    description: s.description,
                    nav_label: s.nav_label,
                    icon: s.icon,
                    sort_order: s.sort_order,
                    is_active: s.is_active,
                  },
                  { headers: auth() }
                );

                if (newKey !== oldKey) {
                  s.section_key = newKey;
                  s.section_key_draft = newKey;
                }
              }
            } catch (err) {
              console.error(`Failed to save section "${s.section_key}":`, err?.response?.data?.message || err.message);
            }

            await Promise.all(
              s.items.map(async (item, itemIdx) => {
                item.sort_order = itemIdx + 1;
                try {
                  const fd = new FormData();
                  fd.append('icon', item.icon ?? '');
                  fd.append('title', item.title ?? '');
                  fd.append('text', item.text ?? '');
                  fd.append('sort_order', item.sort_order);
                  fd.append('is_active', item.is_active ?? 1);
                  if (item._removeImage) {
                    fd.append('remove_image', '1');
                    delete item._removeImage;
                  }
                  if (item._removePdf) {
                    fd.append('remove_pdf', '1');
                    delete item._removePdf;
                  }

                  if (item.role !== null && item.role !== undefined && item.role !== '') {
                    fd.append('role', item.role);
                  }
                  if (item.block_item_id) fd.append('block_item_id', String(item.block_item_id));
                  if (item._file) fd.append('image', item._file);
                  if (item._pdfFile) fd.append('pdf', item._pdfFile);

                  if (item._isNew) {
                    const res = await axios.post(
                      `landing-page-sections/${s.section_key}/items`,
                      fd,
                      { headers: auth() }
                    );
                    if (res.data.success) {
                      item.id = res.data.data.id;
                      item._isNew = false;
                      delete item._file;
                      delete item._pdfFile;
                    }
                  } else {
                    await axios.put(
                      `landing-page-items/${item.id}`,
                      fd,
                      { headers: auth() }
                    );
                    delete item._file;
                    delete item._pdfFile;
                  }
                } catch (err) {
                  console.error(`Failed to save item "${item.title || item.text}" in "${s.section_key}":`, err?.response?.data?.message || err.message);
                }
              })
            );

            const groups = this.getDisplayGroups(s);
            const gallerySlots = groups
              .filter(g => g.isImageGallery)
              .flatMap(g => g.items)
              .filter(slot => slot._file || slot._removeImage);

            await Promise.all(
              gallerySlots.map(async (slot) => {
                const parent = slot._parentItem;
                const wasRemoved = slot._removeImage;
                slot._saving = true;
                try {
                  const fd = new FormData();
                  if (wasRemoved) {
                    fd.append('remove_slot_index', String(slot._slotIndex));
                  } else {
                    fd.append('image', slot._file);
                    fd.append('slot_index', String(slot._slotIndex));
                  }

                  const res = await axios.put(`landing-page-items/${parent.id}`, fd, { headers: auth() });
                  if (res.data.success) {
                    parent.image = res.data.data.image;
                    parent.item_image = res.data.data.item_image;
                    slot._file = null;
                    slot._removeImage = false;
                  }
                } catch (err) {
                  console.error(`Failed to save image slot (idx ${slot._slotIndex}) for parent item ${parent?.id}:`, err?.response?.data?.message || err.message);
                } finally {
                  slot._saving = false;
                }
              })
            );
          })
        );
        this.showToast('All sections and items saved');
      } catch {
        this.showToast('Some sections failed to save', 'error');
      } finally {
        this.saving = false;
      }
    },
    async saveItem(item, section, group) {
      if (item._isImageSlot) {
        return this.saveImageGallerySlot(item);
      }

      const key = this.getItemKey(item);
      const skipPrompt = this.skipBlogPromptKeys.has(key);

      if (this.isNewsletterGroup(group) && !skipPrompt) {
        const makeItBlog = await this.askNewsletterBlogConfirm(item, section);

        if (makeItBlog) {
          return;
        }
      }

      if (skipPrompt) {
        this.skipBlogPromptKeys.delete(key);
      }

      await this.performSaveItem(item, section);
    },
    async performSaveItem(item, section, options = {}) {
      const { forceInactive = false } = options;

      item._saving = true;
      try {
        const fd = new FormData();
        fd.append('icon', item.icon ?? '');
        fd.append('title', item.title ?? '');
        fd.append('text', item.text ?? '');
        fd.append('sort_order', item.sort_order);
        fd.append('is_active', forceInactive ? '0' : (item.is_active ?? 1));
        if (item._removeImage) {
          fd.append('remove_image', '1');
          delete item._removeImage;
        }
        if (item._removePdf) {
          fd.append('remove_pdf', '1');
          delete item._removePdf;
        }

        if (item.role !== null && item.role !== undefined && item.role !== '') {
          fd.append('role', item.role);
        }
        if (item._file) fd.append('image', item._file);
        if (item._pdfFile) fd.append('pdf', item._pdfFile);

        if (item._isNew) {
          const res = await axios.post(
            `landing-page-sections/${section.section_key}/items`,
            fd,
            { headers: auth() }
          );
          if (res.data.success) {
            item.id = res.data.data.id;
            item.pdf = res.data.data.pdf;
            item.image = res.data.data.image;
            item._isNew = false;
            delete item._file;
            delete item._pdfFile;
          }
        } else {
          const res = await axios.put(
            `landing-page-items/${item.id}`,
            fd,
            { headers: auth() }
          );
          if (res.data.success) {
            item.pdf = res.data.data.pdf;
            item.image = res.data.data.image;
          }
          delete item._file;
          delete item._pdfFile;
        }

        if (forceInactive) {
          item.is_active = 0;
        }

        if (!forceInactive) {
          this.showToast('Item saved');
        }
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Failed to save item', 'error');
      } finally {
        item._saving = false;
      }
    },
    async deleteItem(item, section) {
      if (item._isImageSlot) {
        return this.deleteImageGallerySlot(item);
      }

      if (!item.id) {
        const idx = section.items.indexOf(item);
        if (idx !== -1) section.items.splice(idx, 1);
        return;
      }

      const guardKey = `deleteItem_${item.id}`;
      if (!this.pendingActions) this.pendingActions = new Set();
      if (this.pendingActions.has(guardKey)) return;

      const confirmed = await this.confirmDelete({ type: 'item', label: item.title || item.text });
      if (!confirmed) return;

      this.pendingActions.add(guardKey);

      try {
        const hasChildren = section.items.some(i => i.block_item_id === item.id);
        const isChild = item.block_item_id != null;

        if (!hasChildren && !isChild) {
          const fd = new FormData();
          fd.append('is_active', '0');
          if (item.item_image !== null && item.item_image !== undefined) {
            fd.append('item_image', String(item.item_image));
          }
          await axios.put(`landing-page-items/${item.id}`, fd, { headers: auth() });
          item.is_active = 0;
        } else {
          await axios.delete(`landing-page-items/${item.id}`, { headers: auth() });
        }

        if (item.item_image && item.item_image >= 1) {
          for (let i = 0; i < item.item_image; i++) {
            this.$delete(this.slotCache, `${item.id}_${i}`);
          }
        }

        const idx = section.items.indexOf(item);
        if (idx !== -1) section.items.splice(idx, 1);
        this.showToast('Item deleted');
      } catch {
        this.showToast('Failed to delete item', 'error');
      } finally {
        this.pendingActions.delete(guardKey);
      }
    },
    async saveImageGallerySlot(slot) {
      if (!slot._file && !slot._removeImage) return;
      const parent = slot._parentItem;
      const wasRemoved = slot._removeImage;
      slot._saving = true;
      try {
        const fd = new FormData();
        if (wasRemoved) {
          fd.append('remove_slot_index', String(slot._slotIndex));
        } else {
          fd.append('image', slot._file);
          fd.append('slot_index', String(slot._slotIndex));
        }

        const res = await axios.put(`landing-page-items/${parent.id}`, fd, { headers: auth() });

        if (res.data.success) {
          parent.image = res.data.data.image;
          parent.item_image = res.data.data.item_image;
          slot._file = null;
          slot._removeImage = false;
          this.showToast(wasRemoved ? 'Image removed' : 'Image saved');
        }
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Failed to save image', 'error');
      } finally {
        slot._saving = false;
      }
    },
    async deleteImageGallerySlot(slot) {
      const parent = slot._parentItem;

      const confirmed = await this.confirmDelete({ type: 'image', label: parent.title || parent.text || `Image ${slot._slotIndex + 1}` });
      if (!confirmed) return;

      try {
        const fd = new FormData();
        fd.append('remove_image_index', String(slot._slotIndex));

        const res = await axios.put(`landing-page-items/${parent.id}`, fd, { headers: auth() });

        if (res.data.success) {
          parent.image = res.data.data.image;
          parent.item_image = res.data.data.item_image;
          slot._file = null;
          slot.image = null;
          this.$delete(this.slotCache, slot._pendingKey);
          this.showToast('Image removed');
        }
      } catch (err) {
        this.showToast(err?.response?.data?.message || 'Failed to remove image', 'error');
      }
    },
    onPhotoChange(e, item) {
      const file = e.target.files[0];
      if (!file) return;

      if (item._isImageSlot) {
        item._file = file;
        item.image = URL.createObjectURL(file);
        return;
      }

      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      const validExtensions = ['jpg', 'jpeg', 'png'];

      if (!validExtensions.includes(fileExtension)) {
        this.dialogWrongExtension = true;
        e.target.value = null;
        return;
      }

      item._file = file;
      item.image = URL.createObjectURL(file);
    },
    handleDropPhoto(e, item) {
      item._dragOver = false;
      const file = e.dataTransfer.files[0];
      if (!file) return;

      if (item._isImageSlot) {
        item._file = file;
        item.image = URL.createObjectURL(file);
        return;
      }

      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      const validExtensions = ['jpg', 'jpeg', 'png'];

      if (!validExtensions.includes(fileExtension)) {
        this.dialogWrongExtension = true;
        return;
      }

      item._file = file;
      item.image = URL.createObjectURL(file);
      item._removeImage = false;
    },
    removeImage(item) {
      if (item._isImageSlot) {
        item._file = null;
        item.image = null;
        item._removeImage = true;
        this.skipBlogPromptKeys.add(this.getItemKey(item));
        return;
      }
      item._file = null;
      item.image = null;
      item._removeImage = true;
      this.skipBlogPromptKeys.add(this.getItemKey(item));
    },
    onDragCanvasBlock(e, section) {
      this.draggedBlock = section;
      this.draggingNewBlock = null;
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragOverBlock(e, idx) {
      this.draggingOver = true;
      this.dropIndex = idx;
    },
    onDropOnBlock(e, idx) {
      if (this.draggingNewBlock) {
        this.allSections.splice(idx, 0, ...this.allSections.splice(
          this.allSections.findIndex(s => s._key === this.draggingNewBlock._key), 1
        ));
      } else if (this.draggedBlock) {
        const from = this.allSections.findIndex(s => s._key === this.draggedBlock._key);
        const visible = this.visibleSections;
        const toSection = visible[idx];
        const to = this.allSections.findIndex(s => s._key === toSection._key);
        if (from !== -1 && to !== -1 && from !== to) {
          const [moved] = this.allSections.splice(from, 1);
          this.allSections.splice(to, 0, moved);
        }
      }
      this.draggedBlock = null;
      this.draggingNewBlock = null;
      this.draggingOver = false;
      this.dropIndex = null;
    },
    onCanvasDragOver(e) {
      this.draggingOver = true;
      const canvas = e.currentTarget;
      const blocks = canvas.querySelectorAll('.canvas-block');
      let insertIdx = this.visibleSections.length;
      blocks.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        if (e.clientY < rect.top + rect.height / 2) {
          insertIdx = Math.min(insertIdx, i);
        }
      });
      this.dropIndex = insertIdx;
    },
    onCanvasDrop(e) {
      if (this.draggingNewBlock) {
        const visible = this.visibleSections;
        const block = this.draggingNewBlock;
        const from = this.allSections.findIndex(s => s._key === block._key);
        if (this.dropIndex !== null && from !== -1) {
          const target = visible[this.dropIndex];
          if (target) {
            const to = this.allSections.findIndex(s => s._key === target._key);
            if (to !== -1) {
              const [moved] = this.allSections.splice(from, 1);
              this.allSections.splice(to, 0, moved);
            }
          }
        }
      }
      this.draggingNewBlock = null;
      this.draggedBlock = null;
      this.draggingOver = false;
      this.dropIndex = null;
    },
    moveUp(idx) {
      const visible = this.visibleSections;
      if (idx === 0) return;
      const aKey = visible[idx]._key;
      const bKey = visible[idx - 1]._key;
      const ai = this.allSections.findIndex(s => s._key === aKey);
      const bi = this.allSections.findIndex(s => s._key === bKey);

      const sectionA = this.allSections[ai];
      const sectionB = this.allSections[bi];
      this.$set(this.allSections, ai, sectionB);
      this.$set(this.allSections, bi, sectionA);

      this.allSections.forEach((s, i) => { s.sort_order = i + 1; });
    },
    moveDown(idx) {
      const visible = this.visibleSections;
      if (idx === visible.length - 1) return;
      const aKey = visible[idx]._key;
      const bKey = visible[idx + 1]._key;
      const ai = this.allSections.findIndex(s => s._key === aKey);
      const bi = this.allSections.findIndex(s => s._key === bKey);

      const sectionA = this.allSections[ai];
      const sectionB = this.allSections[bi];
      this.$set(this.allSections, ai, sectionB);
      this.$set(this.allSections, bi, sectionA);

      this.allSections.forEach((s, i) => { s.sort_order = i + 1; });
    },
    onDragNewSection(e, sectionType) {
      this.draggingNewBlock = sectionType;
      this.draggedBlock = null;
      e.dataTransfer.effectAllowed = 'copy';
      this.$nextTick(() => this.addNewSection(sectionType).then(s => {
        if (s) this.draggingNewBlock = s;
      }));
    },
    onDragItem(e, item, section) {
      this.draggedItem = item;
      this.draggedItemSection = section;
    },
    onDragOverItem(e, item, section) {
      if (!this.draggedItem || this.draggedItem === item) return;
    },
    onDropOnItem(e, targetItem, section) {
      if (!this.draggedItem || this.draggedItem === targetItem) return;
      if (this.draggedItemSection !== section) return;
      const from = section.items.indexOf(this.draggedItem);
      const to = section.items.indexOf(targetItem);
      section.items.splice(from, 1);
      section.items.splice(to, 0, this.draggedItem);
      this.draggedItem = null;
      this.draggedItemSection = null;
      section.items.forEach((item, idx) => { item.sort_order = idx + 1; });
    },
    onDropItem(e, section) {
      this.draggedItem = null;
      this.draggedItemSection = null;
    },
    showToast(message, type = 'success') {
      this.toast = { show: true, message, type };
      setTimeout(() => { this.toast.show = false; }, 2800);
    },
  },
}
</script>

<style scoped>
.newsletter-confirm-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow:
    0 24px 80px rgba(0, 29, 108, 0.18),
    0 8px 30px rgba(33, 39, 42, 0.12);
}

.newsletter-confirm-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 28px 26px;
  background: #ffffff;
  border-radius: 16px;
}

.newsletter-confirm-icon {
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  border-radius: 15px;
  background: #f2f4f8;
  color: #001d6c;
}

.newsletter-confirm-icon i {
  font-size: 28px;
}

.newsletter-confirm-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 750;
  color: #21272a;
}

.newsletter-confirm-description {
  margin: 0 0 22px;
  font-size: 13px;
  line-height: 1.6;
  color: #697077;
}

.newsletter-confirm-actions {
  display: flex;
  gap: 10px;
  width: 100%;
}

.newsletter-confirm-btn {
  flex: 1;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 9px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.newsletter-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.newsletter-confirm-btn-no {
  border: 1px solid #dfe3e6;
  background: #ffffff;
  color: #4d5358;
}

.newsletter-confirm-btn-no:hover:not(:disabled) {
  background: #f7f8fa;
  color: #21272a;
}

.newsletter-confirm-btn-yes {
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
}

.newsletter-confirm-btn-yes:hover:not(:disabled) {
  background: #1f2937;
  transform: translateY(-1px);
}

.newsletter-confirm-btn-yes i {
  font-size: 15px;
}

.proofread-warning-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow:
    0 24px 80px rgba(0, 29, 108, 0.18),
    0 8px 30px rgba(33, 39, 42, 0.12);
}

.proofread-warning-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 30px 26px 24px;
  background: #ffffff;
  border-radius: 16px;
}

.proofread-warning-icon {
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  border-radius: 14px;
  background: #f2f4f8;
  color: #001d6c;
}

.proofread-warning-icon i {
  font-size: 28px;
}

.proofread-warning-title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 750;
  color: #21272a;
}

.proofread-warning-description {
  margin: 0 0 20px;
  font-size: 13px;
  line-height: 1.6;
  color: #697077;
}

.proofread-warning-btn {
  width: 100%;
  height: 42px;
  border: 1px solid #111827;
  border-radius: 9px;
  background: #111827;
  color: #ffffff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.proofread-warning-btn:hover {
  background: #1f2937;
  transform: translateY(-1px);
}

.dialog-wrong-extension {
  background-image: linear-gradient(
    to bottom,
    #ff8b8b,
    #ff8284,
    #ff787d,
    #ff6e76,
    #ff646f,
    #ff5c6a,
    #ff5464,
    #ff4b5f,
    #ff4359,
    #ff3b54,
    #ff314e,
    #ff2648
  );
}

.color-warning {
  background-image: linear-gradient(
    to right top,
    #dc922b,
    rgb(198, 128, 14),
    #fec864,
    #ffcc5e,
    #ffd057,
    #ffd359,
    #ffd65a,
    #ffd95c,
    #ffdb66,
    #ffde6f,
    #ffe079,
    #ffe282
  );
}

.color-warning-border {
  position: relative;
  border: 2px solid transparent !important;
  border-radius: 28px;
  background-image: linear-gradient(#fff, #fff),
    linear-gradient(
      to right top,
      #dc922b,
      rgb(198, 128, 14),
      #fec864,
      #ffcc5e,
      #ffd057,
      #ffd359,
      #ffd65a,
      #ffd95c,
      #ffdb66,
      #ffde6f,
      #ffe079,
      #ffe282
    );
  background-origin: border-box;
  background-clip: padding-box, border-box;
  color: #dc922b !important;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.cms-page {
  font-family: "Segoe UI", Arial, sans-serif;
  min-height: calc(100vh - 32px);
  margin: 0 16px 16px 16px;
  border-radius: 16px;
  background: #fff;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.cms-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: #fff;
  border-bottom: 1px solid #eef1f4;
  position: sticky;
  top: 0;
}

.cms-header-left {
  min-width: 0;
  flex: 1 1 auto;
}

.cms-title-wrapper {
  display: flex;
  align-items: flex-start;
}

.cms-icon-box {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #0471a5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.cms-icon-box i {
  color: white;
  font-size: 34px;
}

.cms-title {
  margin: 0;
  padding: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272a;
}

.cms-title i {
  color: #0b6599;
}

.cms-subtitle {
  margin: 4px 0 0 0;
  padding: 0;
  font-size: 16px;
  color: #8a8f98;
  line-height: 1.5;
}

.cms-header-actions {
  display: flex;
  gap: 10px;
}

.btn-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 20px;
  min-height: 44px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.btn-header i {
  font-size: 18px;
}

.btn-header-save {
  background: #0471a5;
  color: #fff;
  border-color: #0471a5;
  box-shadow: 0 4px 12px rgba(4, 113, 165, 0.18);
}

.cms-loading,
.cms-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  padding: 80px 24px;
  color: #697077;
  font-size: 14px;
}

.cms-error {
  color: #ef4444;
}

.cms-error i {
  font-size: 40px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #0b6599;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn-retry {
  padding: 8px 20px;
  border: 1px solid #ef4444;
  color: #ef4444;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.cms-builder {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 0;
  flex: 1;
  height: calc(100vh - 61px);
  overflow: hidden;
}

.sidebar {
  background: #fff;
  border-right: 1px solid #e6e8eb;
  padding: 24px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef1f4;
}

.sidebar-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.sidebar-label {
  position: relative;
  padding-left: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #21272a;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.sidebar-label::before {
  content: "";
  position: absolute;
  left: 0;
  top: 2px;
  width: 4px;
  height: 16px;
  border-radius: 999px;
  background: #0471a5;
}

.sidebar-hint {
  margin-top: -15px;
  font-size: 14px;
  line-height: 1.5;
  color: #8a8f98;
}

.section-library {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1.5px dashed #dde1e6;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #21272a;
  cursor: grab;
  background: #f2f4f8;
  transition: border-color 0.15s, background 0.15s;
  user-select: none;
}

.section-chip i {
  color: #0b6599;
  font-size: 16px;
}

.section-chip:hover {
  border-color: #0b6599;
  background: #e8f2fb;
}

.section-chip:active {
  cursor: grabbing;
}

.canvas {
  overflow-y: auto;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
}

.canvas-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  color: #c1c7cd;
  border: 2px dashed #dde1e6;
  border-radius: 16px;
  margin: 20px 0;
}

.canvas-empty i {
  font-size: 48px;
}

.canvas-empty p {
  font-size: 14px;
}

.drop-indicator {
  height: 3px;
  background: #0b6599;
  border-radius: 2px;
  margin: -8px 0 8px;
  opacity: 0.8;
}

.canvas-block {
  border: 1.5px solid #dde1e6;
  border-radius: 12px;
  background: #ffffff;
  margin-bottom: 16px;
  transition: border-color 0.15s, box-shadow 0.15s;
  overflow: hidden;
}

.canvas-block:hover {
  border-color: #0b6599;
}

.canvas-block--active {
  border-color: #0b6599;
  box-shadow: 0 0 0 3px rgba(11, 101, 153, 0.12);
}

.block-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #f2f4f8;
  border-bottom: 1px solid #dde1e6;
  cursor: pointer;
}

.block-toolbar-handle {
  font-size: 20px;
  color: #c1c7cd;
  cursor: grab;
  line-height: 1;
}

.block-toolbar-key {
  font-size: 12px;
  font-weight: 700;
  color: #0b6599;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.block-toolbar-spacer {
  flex: 1;
}

.toolbar-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #697077;
  font-size: 16px;
  display: flex;
  align-items: center;
  padding: 4px 6px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
}

.toolbar-btn:hover {
  color: #0b6599;
  background: #e8f2fb;
}

.toolbar-btn--danger:hover {
  color: #ef4444;
  background: #fee2e2;
}

.toolbar-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.block-preview {
  padding: 14px 20px;
  cursor: pointer;
}

.block-preview-subtitle {
  font-size: 11px;
  font-weight: 700;
  color: #001d6c;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 4px;
}

.block-preview-title {
  font-size: 16px;
  font-weight: 600;
  color: #21272a;
}

.block-preview-meta {
  font-size: 12px;
  color: #697077;
  margin-top: 4px;
}

.block-editor {
  padding: 20px;
  border-top: 1px solid #dde1e6;
}

.editor-cols {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 24px;
  align-items: start;
}

.editor-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.editor-col-title {
  font-size: 12px;
  font-weight: 700;
  color: #697077;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-label {
  font-size: 11px;
  font-weight: 600;
  color: #697077;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.field-input {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #dde1e6;
  border-radius: 7px;
  font-size: 13px;
  color: #21272a;
  outline: none;
  width: 100%;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.field-input:focus {
  border-color: #0b6599;
  box-shadow: 0 0 0 2px rgba(11, 101, 153, 0.1);
}

.field-input--sm {
  font-size: 12px;
  height: 32px;
}

.field-input--full {
  width: 100%;
  grid-column: 1 / -1;
}

.field-textarea {
  padding: 8px 10px;
  border: 1px solid #dde1e6;
  border-radius: 7px;
  font-size: 13px;
  color: #21272a;
  outline: none;
  resize: vertical;
  width: 100%;
  font-family: inherit;
  transition: border-color 0.15s;
}

.field-textarea:focus {
  border-color: #0b6599;
  box-shadow: 0 0 0 2px rgba(11, 101, 153, 0.1);
}

.btn-save-section {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #0b6599;
  color: #ffffff;
  border: none;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  align-self: flex-start;
  transition: opacity 0.2s;
  margin-top: 4px;
}

.btn-save-section:hover {
  opacity: 0.9;
}

.btn-save-section:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.btn-add-block-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1.5px solid #0471a5;
  border-radius: 8px;
  background: #fff;
  color: #0471a5;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.btn-add-block-item:hover {
  background: #e8f2fb;
}

.block-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.block-picker-dialog {
  background: #fff;
  border-radius: 14px;
  width: 520px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.block-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}

.block-picker-title {
  font-size: 16px;
  font-weight: 700;
  color: #21272a;
  margin: 0;
}

.block-picker-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #697077;
  padding: 0;
  line-height: 1;
}

.block-picker-close:hover {
  color: #21272a;
}

.block-picker-hint {
  padding: 6px 24px 12px;
  font-size: 13px;
  color: #8a8f98;
  margin: 0;
}

.block-picker-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.block-picker-item {
  border: 1.5px solid #dde1e6;
  border-radius: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.block-picker-item:hover {
  border-color: #0471a5;
  background: #f4f9fd;
}

.block-picker-item--selected {
  border-color: #0471a5;
  background: #e8f2fb;
}

.block-picker-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.block-picker-item-header i {
  font-size: 18px;
  color: #0471a5;
}

.block-picker-item-label {
  font-size: 14px;
  font-weight: 600;
  color: #21272a;
}

.block-picker-item-preview {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 8px;
  border-left: 2px solid #dde1e6;
}

.block-picker-preview-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #697077;
}

.block-picker-preview-row i {
  font-size: 14px;
  color: #8a8f98;
}

.block-picker-preview-img-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  background: #eef1f4;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  color: #697077;
  margin-left: auto;
}

.block-picker-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #eef1f4;
}

.btn-block-cancel {
  padding: 8px 18px;
  border: 1px solid #dde1e6;
  border-radius: 8px;
  background: #fff;
  color: #697077;
  font-size: 13px;
  cursor: pointer;
}

.btn-block-cancel:hover {
  background: #f2f4f8;
}

.btn-block-confirm {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  background: #0471a5;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-block-confirm:hover:not(:disabled) {
  background: #035f8a;
}

.btn-block-confirm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.item-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 32px;
}

.item-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item-block--grouped {
  border: 1.5px solid #dde1e6;
  border-radius: 12px;
  padding: 10px;
  background: #EEF1F4;
  gap: 6px;
}

.item-block--grouped .item-row {
  background: #F2F4F8;
  border-color: #DDE1E6;
}

.item-block-header {
  display: flex;
  justify-content: flex-end;
  padding: 4px 4px 6px;
}

.item-block-label {
  font-size: 11px;
  font-weight: 700;
  color: #697077;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.btn-add-item-group {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1.5px solid #0471A5;
  border-radius: 6px;
  background: #fff;
  color: #0471A5;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-add-item-group:hover {
  background: #e8f2fb;
}

.btn-delete-block {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1.5px solid #dc2626;
  border-radius: 6px;
  background: #fff;
  color: #dc2626;
  font-size: 14px;
  cursor: pointer;
  margin-left: 10px;
  transition: background 0.15s;
}

.btn-delete-block:hover {
  background: #fef2f2;
}

.items-empty {
  font-size: 12px;
  color: #c1c7cd;
  padding: 10px 0;
}

.item-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #dde1e6;
  border-radius: 8px;
  background: #f2f4f8;
  user-select: none;
}

.item-drag {
  font-size: 18px;
  color: #c1c7cd;
  cursor: grab;
  flex-shrink: 0;
  margin-top: 6px;
}

.item-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item-field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.item-photo-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
}

.drop-zone-uploaded {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.item-photo {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #dde1e6;
  flex-shrink: 0;
}

.item-photo--wide {
  width: 120px;
  height: 72px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #dde1e6;
}

.btn-remove-image {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #0d6efd;
  color: #fff;
  cursor: pointer;
}

.btn-remove-image:hover {
  background: #0954c6;
}

.drop-zone-upload {
  position: relative;
  width: 100%;
  min-height: 120px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  transition: 0.2s;
  cursor: pointer;
  overflow: hidden;
}

.drop-zone-upload:hover {
  border-color: #1976d2;
}

.drop-zone-upload-over {
  background: #eef6ff;
  border-color: #1976d2;
}

.drop-zone-upload input {
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.drop-zone-upload-info {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #777;
  pointer-events: none;
}

.drop-zone-icon {
  font-size: 34px;
  color: #8d8d8d;
}

.drop-zone-title {
  margin-top: 8px;
  font-size: 14px;
}

.item-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  margin-top: 2px;
}

.item-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 5px;
  transition: color 0.15s, background 0.15s;
  color: #697077;
}

.item-btn--save:hover {
  color: #0b6599;
  background: #e8f2fb;
}

.item-btn--edit:hover {
  color: #0b6599;
  background: #e8f2fb;
}

.item-btn--delete:hover {
  color: #ef4444;
  background: #fee2e2;
}

.item-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cms-toast {
  position: fixed;
  top: 20px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-80px);
  opacity: 0;
  transition: transform 0.3s, opacity 0.3s;
  z-index: 100;
  pointer-events: none;
}

.cms-toast--show {
  transform: translateY(0);
  opacity: 1;
}

.cms-toast--success {
  background: #065f46;
}

.cms-toast--error {
  background: #991b1b;
}

@media (max-width: 1024px) {
  .cms-page {
    margin: 0 12px 12px 12px;
    overflow-x: hidden;
  }

  .cms-header {
    padding: 20px 24px;
  }

  .cms-icon-box {
    width: 48px;
    height: 48px;
    margin-right: 12px;
  }

  .cms-icon-box i {
    font-size: 28px;
  }

  .cms-title {
    font-size: 20px;
  }

  .cms-subtitle {
    font-size: 14px;
  }

  .cms-builder {
    grid-template-columns: 220px 1fr;
    overflow: hidden;
  }

  .sidebar {
    padding: 20px 16px;
    gap: 22px;
    overflow-x: hidden;
  }

  .canvas {
    padding: 20px 24px;
    overflow-x: hidden;
    min-width: 0;
    box-sizing: border-box;
  }

  .canvas-block {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .block-toolbar {
    flex-wrap: nowrap;
    align-items: center;
  }

  .block-toolbar-key {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .block-editor {
    padding: 20px;
    min-width: 0;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .editor-cols {
    grid-template-columns: 1fr;
    gap: 20px;
    min-width: 0;
  }

  .editor-col {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .field-input,
  .field-textarea {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .item-field-grid {
    grid-template-columns: 1fr 1fr;
    min-width: 0;
  }

  .items-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .btn-add-block-item {
    box-sizing: border-box;
    white-space: nowrap;
  }

  .item-block {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .item-block--grouped {
    padding: 10px;
    box-sizing: border-box;
    width: 100%;
  }

  .item-block-header {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-right: 4px;
  }

  .btn-add-item-group {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .btn-delete-block {
    flex-shrink: 0;
    margin-left: 0;
  }

  .item-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding-right: 10px;
  }

  .item-fields {
    min-width: 0;
    flex: 1 1 auto;
  }

  .item-actions {
    flex-shrink: 0;
    flex-grow: 0;
    margin-left: 6px;
  }

  .drop-zone-uploaded,
  .drop-zone-upload {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .item-photo,
  .item-photo--wide {
    max-width: 100%;
  }

  .block-picker-dialog {
    width: 90%;
    max-width: 480px;
    box-sizing: border-box;
  }
}

@media (max-width: 768px) {
  .cms-page {
    margin: 0 8px 8px 8px;
    border-radius: 12px;
    overflow-x: hidden;
  }

  .cms-header {
    flex-direction: column;
    align-items: stretch;
    padding: 16px 20px;
    gap: 14px;
  }

  .cms-header-left {
    width: 100%;
  }

  .cms-header-actions {
    width: 100%;
  }

  .btn-header {
    flex: 1 1 auto;
    width: 100%;
  }

  .cms-builder {
    grid-template-columns: 200px 1fr;
    height: calc(100vh - 61px);
    overflow: hidden;
  }

  .sidebar {
    padding: 16px 12px;
    gap: 18px;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: none;
  }

  .sidebar-section {
    padding-bottom: 14px;
  }

  .section-library {
    flex-direction: column;
  }

  .section-chip {
    font-size: 12.5px;
    padding: 9px 10px;
    white-space: normal;
  }

  .canvas {
    padding: 16px;
    height: auto;
    overflow-x: hidden;
    min-width: 0;
    box-sizing: border-box;
  }

  .canvas-block {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .block-toolbar {
    flex-wrap: nowrap;
    align-items: center;
    padding: 10px 12px;
  }

  .block-toolbar-key {
    order: 0;
    flex-basis: auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .block-editor {
    padding: 16px;
    min-width: 0;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .editor-cols {
    grid-template-columns: 1fr;
    gap: 20px;
    min-width: 0;
  }

  .editor-col {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .field-input,
  .field-textarea {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .item-field-grid {
    grid-template-columns: 1fr;
    min-width: 0;
  }

  .items-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .btn-add-block-item {
    width: 100%;
    justify-content: center;
    box-sizing: border-box;
    white-space: nowrap;
  }

  .item-block {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .item-block--grouped {
    padding: 10px;
    box-sizing: border-box;
    width: 100%;
  }

  .item-block-header {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-right: 4px;
  }

  .btn-add-item-group {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .btn-delete-block {
    flex-shrink: 0;
    margin-left: 0;
  }

  .item-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding-right: 10px;
  }

  .item-fields {
    min-width: 0;
    flex: 1 1 auto;
  }

  .item-actions {
    flex-shrink: 0;
    flex-grow: 0;
    margin-left: 6px;
  }

  .drop-zone-uploaded,
  .drop-zone-upload {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .item-photo,
  .item-photo--wide {
    max-width: 100%;
  }

  .btn-remove-image {
    width: 100%;
    box-sizing: border-box;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .cms-header {
    padding: 14px 16px;
  }

  .cms-title-wrapper {
    align-items: center;
  }

  .cms-icon-box {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    margin-right: 10px;
  }

  .cms-icon-box i {
    font-size: 22px;
  }

  .cms-title {
    font-size: 17px;
  }

  .cms-subtitle {
    font-size: 12.5px;
    line-height: 1.4;
  }

  .btn-header {
    padding: 8px 14px;
    font-size: 14px;
    min-height: 40px;
  }

  .btn-header i {
    font-size: 16px;
  }

  .cms-builder {
    display: flex;
    flex-direction: column;
    height: auto;
    overflow: visible;
  }

  .sidebar {
    width: 100%;
    max-height: 240px;
    padding: 12px;
    gap: 14px;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    border-right: none;
    border-bottom: 1px solid #e6e8eb;
    box-sizing: border-box;
  }

  .sidebar-section {
    padding-bottom: 12px;
  }

  .sidebar-label {
    font-size: 12px;
  }

  .sidebar-hint {
    font-size: 12px;
    margin-top: -8px;
  }

  .section-library {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }

  .section-chip {
    flex: 1 1 auto;
    font-size: 11.5px;
    padding: 8px 8px;
    white-space: nowrap;
  }

  .canvas {
    width: 100%;
    padding: 12px;
    height: auto;
    overflow-x: hidden;
    min-width: 0;
    box-sizing: border-box;
  }

  .canvas-block {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .canvas-empty {
    padding: 50px 16px;
  }

  .canvas-empty i {
    font-size: 36px;
  }

  .block-toolbar {
    flex-wrap: nowrap;
    align-items: center;
    padding: 8px 12px;
  }

  .block-toolbar-key {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .block-preview {
    padding: 12px 14px;
  }

  .block-preview-title {
    font-size: 14px;
  }

  .block-editor {
    padding: 12px;
    min-width: 0;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .editor-cols {
    grid-template-columns: 1fr;
    gap: 16px;
    min-width: 0;
  }

  .editor-col {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .editor-col-title {
    font-size: 11px;
  }

  .field-input,
  .field-textarea {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    font-size: 13px;
  }

  .item-field-grid {
    grid-template-columns: 1fr;
    min-width: 0;
  }

  .btn-save-section {
    width: 100%;
    justify-content: center;
    box-sizing: border-box;
  }

  .items-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .btn-add-block-item {
    width: 100%;
    justify-content: center;
    box-sizing: border-box;
    white-space: nowrap;
  }

  .item-block {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .item-block--grouped {
    padding: 8px;
    box-sizing: border-box;
    width: 100%;
  }

  .item-block-header {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding-right: 4px;
  }

  .btn-add-item-group {
    white-space: nowrap;
    flex-shrink: 0;
    padding: 4px 8px;
    font-size: 11.5px;
  }

  .btn-delete-block {
    flex-shrink: 0;
    margin-left: 0;
    width: 26px;
    height: 26px;
  }

  .item-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
    padding-right: 8px;
  }

  .item-fields {
    min-width: 0;
    flex: 1 1 auto;
  }

  .item-actions {
    flex-direction: column;
    flex-shrink: 0;
    flex-grow: 0;
    margin-left: 6px;
  }

  .drop-zone-uploaded,
  .drop-zone-upload {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .item-photo--wide {
    width: 100px;
    height: 60px;
    max-width: 100%;
  }

  .btn-remove-image {
    width: 100%;
    box-sizing: border-box;
    text-align: center;
  }

  .block-picker-header {
    padding: 16px 16px 0;
  }

  .block-picker-hint {
    padding: 6px 16px 10px;
  }

  .block-picker-list {
    padding: 0 16px;
  }

  .block-picker-footer {
    padding: 12px 16px;
    flex-wrap: wrap;
  }

  .btn-block-cancel,
  .btn-block-confirm {
    flex: 1 1 auto;
    justify-content: center;
  }
}
</style>